import Phaser from 'phaser';
import SocketManager from '../network/SocketManager';
import type { RoomState, PublicPlayerState, RoundResult, OnlineStats } from '@shared/types';
import { GameSymbol, ComboType, PlayerStatus, RoomStatus } from '@shared/enums';
import { REEL_COUNT, ROUND_DURATION_MS, ROUND_PAUSE_MS } from '@shared/game-constants';
import { validateCombo } from '@shared/combo-validator';
import { center } from '../ui/DesignSystem';
import { normalizeRoomCode, setAppRoute } from '../router';
import {
  playSfx,
  SFX_FINISH_KEY,
  SFX_POINTS_KEY,
  SFX_SPIN_WHEEL_KEY,
  SFX_TAP_KEY,
} from '../audio/sfx';
import roundNotificationManager from '../native/RoundNotificationManager';
import { getEquippedIconTextures, getSelectedTheme, getSymbolOverride, getSymbolOverrideTexture } from '../features/FeatureState';

// ── Extracted constants & types (formerly defined inline) ─────────────────
import {
  SYM,
  SPECIAL_FLAG_TEXTURE_KEY, SPECIAL_X_TEXTURE_KEY, SPECIAL_JOKER_TEXTURE_KEY,
  SPECIAL_FLAG_ASSET_URL, SPECIAL_X_ASSET_URL, SPECIAL_JOKER_ASSET_URL,
  INPUT_HIT_ALPHA, COMBO_LABELS, GENERIC_COMBOS,
} from './game/constants';
import { ThemeVisual, THEME_VISUALS, hexColor } from './game/themes';
import type { GameLayoutProfile, SpecialSpinKind, GameLayout } from './game/layouts';
import { GAME_LAYOUTS } from './game/layouts';

// Legacy fallback palette (a few spots still reference CABINET directly)
const CABINET = {
  strokeSoft: 0x6838bb,
  text: '#f0e8ff',
};
export class GameScene extends Phaser.Scene {
  private sm!: SocketManager;
  private roomState!: RoomState;
  private myPlayer!: PublicPlayerState;
  private theme: ThemeVisual = THEME_VISUALS.classic;
  private layout: GameLayout = GAME_LAYOUTS.landscape;
  private gameRefXOffset = 0;
  private comboPanelBgX = 0;
  private comboPanelBgW = 0;

  /* Top bar */
  private roundText!: Phaser.GameObjects.Text;
  private roundScoreText!: Phaser.GameObjects.Text;
  private timerBar!: Phaser.GameObjects.Rectangle;
  private timerBarBg!: Phaser.GameObjects.Rectangle;
  private scoreText!: Phaser.GameObjects.Text;
  private timerBarFullW = 0;
  private onlineCounterBg?: Phaser.GameObjects.Rectangle;
  private onlineCounterText?: Phaser.GameObjects.Text;
  private onlineStats: OnlineStats | null = null;

  /* Reels */
  private reelBgs: Phaser.GameObjects.Rectangle[] = [];
  private reelTexts: Phaser.GameObjects.Text[] = [];
  private reelGlows: Phaser.GameObjects.Rectangle[] = [];
  private lockIcons: Phaser.GameObjects.Text[] = [];
  private reelGhostTopTexts: Phaser.GameObjects.Text[] = [];
  private reelGhostBottomTexts: Phaser.GameObjects.Text[] = [];
  private reelSprites: Phaser.GameObjects.Image[] = [];
  private reelGhostTopSprites: Phaser.GameObjects.Image[] = [];
  private reelGhostBottomSprites: Phaser.GameObjects.Image[] = [];
  private reelHitAreas: Phaser.GameObjects.Rectangle[] = [];
  private reelAuras: Phaser.GameObjects.Rectangle[] = [];

  /* Draw Button */
  private drawBtnBg!: Phaser.GameObjects.Rectangle;
  private drawBtnText!: Phaser.GameObjects.Text;
  private drawBtnAltText!: Phaser.GameObjects.Text;
  private drawBtnAltBg!: Phaser.GameObjects.Rectangle;
  private drawBtnHaloL!: Phaser.GameObjects.Rectangle;
  private drawBtnHaloR!: Phaser.GameObjects.Rectangle;
  private drawBtnGfxL!: Phaser.GameObjects.Graphics;
  private drawBtnGfxR!: Phaser.GameObjects.Graphics;
  private drawBtnGeom = { w: 0, h: 0, rad: 0 };

  /* Containers */
  private centerX = 0;
  private centerY = 0;
  private mainW = 0;
  private mainH = 0;
  private cabinetLeft = 0;
  private cabinetTop = 0;
  private cabinetScale = 1;
  private headerLeft = 0;
  private headerRight = 0;
  private headerTop = 0;
  private headerBottom = 0;
  private headerRowTopY = 0;
  private headerRowBottomY = 0;
  private headerTimerX = 0;
  private headerTimerW = 0;
  private headerCellW = 0;
  private headerCellH = 0;
  private reelY = 0;
  private reelSize = 86;
  private comboPanelX = 0;
  private comboPanelW = 220;
  private leftAreaCx = 0;
  private headerBottomEdge = 0;
  private flexComboCenterY = 0;
  private flexDrawY = 0;
  private flexReelCenterY = 0;
  private reelVisualW = 86;
  private reelVisualH = 300;

  private safeLeft = 0;
  private safeRight = 0;
  private safeTop = 0;
  private safeBottom = 0;
  private safeW = 0;
  private safeH = 0;
  private safeCx = 0;
  private safeCy = 0;

  /* Combos */
  private comboBgs: Phaser.GameObjects.Rectangle[] = [];
  private comboScoreTexts: Phaser.GameObjects.Text[] = [];
  private comboTypes: ComboType[] = [];
  private comboRightBgs: Phaser.GameObjects.Rectangle[] = [];
  private comboPreviewRows: Phaser.GameObjects.Container[] = [];
  private comboPulseGlows: Phaser.GameObjects.Rectangle[] = [];
  private comboGlowTweens: Array<Phaser.Tweens.Tween | null> = [];

  /* Draw button usage tracking (separate left/right) */
  private leftDrawUsed = false;
  private rightDrawUsed = false;

  /* Info overlay */
  private infoText!: Phaser.GameObjects.Text;
  private homeBtnBg?: Phaser.GameObjects.Rectangle;
  private homeBtnText?: Phaser.GameObjects.Text;
  private homeBtnGfx?: Phaser.GameObjects.Graphics;
  private timerEvent?: Phaser.Time.TimerEvent;
  private spinReleaseTimer?: Phaser.Time.TimerEvent;
  private spinning = false;
  private routeRoomId = '';
  private resizeTimer?: Phaser.Time.TimerEvent;
  private resizeSignature = '';
  private keydownHandler?: (event: KeyboardEvent) => void;
  private timerShine?: Phaser.GameObjects.Rectangle;
  private ambientFxTweens: Phaser.Tweens.Tween[] = [];
  private lockStateCache: boolean[] = Array(REEL_COUNT).fill(false);
  private lockCachePrimed = false;
  private pendingSpecialSpin: SpecialSpinKind = 'none';
  private activeSpecialSpin: SpecialSpinKind = 'none';
  private reelOverrideSpecialSpin: SpecialSpinKind = 'none';
  private specialFlagSpinDone = false;
  private specialCrossSpinDone = false;
  private resyncInFlight = false;
  private lastResyncAttemptAt = 0;
  private navigatingHome = false;
  private appForegroundHandler?: () => void;

  /* Round-end popup elements (so we can destroy them early) */
  private roundEndPopup: Phaser.GameObjects.GameObject[] = [];
  private pendingRoundEndResult: RoundResult | null = null;

  /*
   * Ghost (top/bottom) reel symbols are normally chosen at random during a
   * spin animation and not persisted anywhere. When the device rotates we
   * call scene.restart(), which would otherwise revert every ghost to the
   * default STAR texture set during buildReels(). These two caches survive
   * init() (intentionally not cleared there) so the ghost row carries over
   * across orientation changes.
   */
  private ghostTopSyms: GameSymbol[] = [];
  private ghostBottomSyms: GameSymbol[] = [];

  constructor() {
    super({ key: 'GameScene' });
  }

  preload(): void {
    Object.values(SYM).forEach((cfg) => {
      if (!this.textures.exists(cfg.textureKey)) {
        this.load.image(cfg.textureKey, cfg.assetUrl);
      }
    });
    if (!this.textures.exists(SPECIAL_FLAG_TEXTURE_KEY)) {
      this.load.image(SPECIAL_FLAG_TEXTURE_KEY, SPECIAL_FLAG_ASSET_URL);
    }
    if (!this.textures.exists(SPECIAL_X_TEXTURE_KEY)) {
      this.load.image(SPECIAL_X_TEXTURE_KEY, SPECIAL_X_ASSET_URL);
    }
    if (!this.textures.exists(SPECIAL_JOKER_TEXTURE_KEY)) {
      this.load.image(SPECIAL_JOKER_TEXTURE_KEY, SPECIAL_JOKER_ASSET_URL);
    }
    getEquippedIconTextures().forEach((item) => {
      if (!this.textures.exists(item.key)) {
        this.load.image(item.key, item.url);
      }
    });
  }

  init(data?: { room?: RoomState }): void {
    this.reelBgs = [];
    this.reelTexts = [];
    this.reelGlows = [];
    this.lockIcons = [];
    this.reelGhostTopTexts = [];
    this.reelGhostBottomTexts = [];
    this.reelSprites = [];
    this.reelGhostTopSprites = [];
    this.reelGhostBottomSprites = [];
    this.reelHitAreas = [];
    this.reelAuras = [];
    this.comboBgs = [];
    this.comboScoreTexts = [];
    this.comboTypes = [];
    this.comboRightBgs = [];
    this.comboPreviewRows = [];
    this.comboPulseGlows = [];
    this.comboGlowTweens.forEach((tw) => tw?.remove());
    this.comboGlowTweens = [];
    this.stopAmbientFx();
    this.leftDrawUsed = false;
    this.rightDrawUsed = false;
    this.spinning = false;
    this.spinReleaseTimer?.destroy();
    this.spinReleaseTimer = undefined;
    this.routeRoomId = '';
    this.roundEndPopup = [];
    this.lockStateCache = Array(REEL_COUNT).fill(false);
    this.lockCachePrimed = false;
    this.pendingSpecialSpin = 'none';
    this.activeSpecialSpin = 'none';
    this.reelOverrideSpecialSpin = 'none';
    this.specialFlagSpinDone = false;
    this.specialCrossSpinDone = false;
    this.resyncInFlight = false;
    this.lastResyncAttemptAt = 0;
    this.navigatingHome = false;
    this.appForegroundHandler = undefined;
    this.pendingRoundEndResult = null;
    this.timerShine = undefined;
    this.onlineCounterBg = undefined;
    this.onlineCounterText = undefined;
    this.onlineStats = null;
    this.homeBtnGfx = undefined;
    this.roundScoreText = undefined!;
    this.roomState = undefined!;
    this.myPlayer = undefined!;
    if (data?.room) {
      this.roomState = data.room;
    }
  }

  create(): void {
    this.sm = SocketManager.getInstance();
    this.theme = this.resolveThemeVisual();
    this.prepareCanvasInputLayer();
    void roundNotificationManager.init();
    void roundNotificationManager.cancelNextRoundNotification();
    const { w, h } = center(this);
    this.resolveSafeArea(w, h);
    this.layout = this.pickLayout(this.safeW, this.safeH);
    this.computeLayoutDerivedValues();
    this.resizeSignature = this.makeResizeSignature(w, h);

    this.centerX = this.safeCx;
    this.centerY = this.safeCy;
    // Always keep the full game layout visible (no UI cropping).
    const fitScale = Math.min(this.safeW / this.layout.refW, this.safeH / this.layout.refH);
    this.cabinetScale = fitScale;
    this.mainW = Math.floor(this.layout.refW * this.cabinetScale);
    this.mainH = Math.floor(this.layout.refH * this.cabinetScale);
    this.cabinetLeft = this.centerX - this.mainW / 2;
    this.cabinetTop = this.centerY - this.mainH / 2;

    this.comboPanelW = this.scaleX(this.layout.rightCellLeft.w + this.layout.rightCellRight.w + 20);
    this.comboPanelX = this.scalePos((this.layout.rightCellLeft.x + this.layout.rightCellRight.x) / 2);
    this.leftAreaCx = this.scalePos(this.layout.leftAreaAnchorX);
    this.reelSize = this.scaleX(this.layout.reelW);
    
    // Dynamic Layout Anchors
    const portrait = this.layout.profile === 'portrait';
    
    // Header always on top
    const headerPadY = this.scaleX(8);
    const rowGapD = this.scaleX(10);
    const rowHD = this.scaleX(portrait ? 42 : 40);
    const headerH = rowHD * 2 + rowGapD;
    this.headerBottomEdge = this.safeTop + headerPadY + headerH;
    
    // Portrait: Combos at Bottom, Reels/Draw in Center
    // Landscape: Draw at Bottom, Reels/Combos in Center
    
    if (portrait) {
      const comboTop = this.comboGridTopY();
      const comboBottom = this.comboGridBottomY();
      const contentTop = this.headerBottomEdge + this.scaleX(18);
      const contentBottom = comboTop - this.scaleX(22);
      const contentCenterY = (contentTop + contentBottom) / 2;
      const drawH = this.scaleX(this.layout.drawLeft.h - 8);
      const reelH = this.scaleX(this.layout.reelH - 6);
      const reelDrawGap = this.scaleX(34);
      const blockH = reelH + reelDrawGap + drawH;

      this.flexComboCenterY = (comboTop + comboBottom) / 2;
      this.flexReelCenterY = contentCenterY - blockH / 2 + reelH / 2;
      this.flexDrawY = contentCenterY + blockH / 2 - drawH / 2;

      const totalReelsW = this.safeW - this.scaleX(8);
      this.reelSize = totalReelsW / 5;
      this.reelY = this.flexReelCenterY;
    } else {
      const availableAreaT = this.headerBottomEdge + this.scaleX(10);
      const availableAreaB = this.safeBottom - this.scaleX(10);
      const availableAreaH = availableAreaB - availableAreaT;
      const areaCenterY = availableAreaT + availableAreaH / 2;

      this.flexDrawY = availableAreaB - this.scaleX(35);
      this.flexReelCenterY = areaCenterY - this.scaleX(20);
      this.flexComboCenterY = this.flexReelCenterY;

      const reelStartX = this.landscapeReelStartX();
      const comboPanelLeftX = this.scalePos(this.comboPanelBgX - this.comboPanelBgW / 2) + this.comboOffsetX();
      const totalReelsW = Math.max(this.scaleX(80 * REEL_COUNT), comboPanelLeftX - reelStartX - this.scaleX(14));
      this.reelSize = totalReelsW / REEL_COUNT;
      this.reelY = this.flexReelCenterY;
    }
    this.reelVisualW = this.reelSize + this.scaleX(2);
    this.reelVisualH = this.scaleX(this.layout.reelH - 6);


    this.buildCabinetBackground(w, h);
    this.buildMainFrame();
    this.buildSymbolStrip();
    this.buildTopBar(w);
    this.buildOnlineCounter();
    this.buildReels();
    this.buildDrawButton();
    this.buildComboPanel(w, h);
    this.buildInfoOverlay();
    this.buildHomeButton();
    this.startAmbientFx();
    this.setupSocketListeners();
    this.setupForegroundSync();
    this.setupKeyboardControls();
    this.input.on('gameobjectdown', this.handleGameObjectDown, this);
    this.startTimer();
    this.scale.on('resize', this.handleResize, this);

    this.events.once('shutdown', () => {
      this.cleanupListeners();
      this.timerEvent?.destroy();
      this.spinReleaseTimer?.destroy();
      this.removeKeyboardControls();
      this.input.off('gameobjectdown', this.handleGameObjectDown, this);
      this.scale.off('resize', this.handleResize, this);
      this.resizeTimer?.destroy();
      this.stopAmbientFx();
      this.teardownForegroundSync();
      void roundNotificationManager.cancelNextRoundNotification();
    });

    // Initialize from passed room state (e.g. from GAME_START)
    if (this.roomState) {
      this.findMe();
      this.syncGameRoute();
      if (this.myPlayer) {
        this.queueSpecialSpinForCurrentState();
        this.refresh();
      } else {
        this.requestStateResync('init-without-player');
      }
    } else {
      this.requestStateResync('init-without-room');
    }
  }

  private scalePos(x: number): number {
    return this.cabinetLeft + (x + this.gameRefXOffset) * this.cabinetScale;
  }

  private prepareCanvasInputLayer(): void {
    if (typeof document === 'undefined') return;
    document.body.removeAttribute('data-pickitup-view');
    const uiLayer = document.getElementById('ui-layer');
    if (!uiLayer) return;
    uiLayer.innerHTML = '';
    uiLayer.scrollTop = 0;
    uiLayer.scrollLeft = 0;
  }

  private scalePosY(y: number): number {
    return this.cabinetTop + y * this.cabinetScale;
  }

  private scaleX(v: number): number {
    return v * this.cabinetScale;
  }

  private landscapeReelStartX(): number {
    return this.safeLeft + this.scaleX(4);
  }

  private comboOffsetX(): number {
    return this.layout.profile === 'landscape' ? this.scaleX(30) : 0;
  }

  private comboOffsetY(): number {
    return 0;
  }

  private reelCenterX(index: number): number {
    const startX = this.layout.profile === 'portrait'
      ? this.safeLeft + this.scaleX(4)
      : this.landscapeReelStartX();
    return startX + this.reelSize / 2 + index * this.reelSize;
  }

  private reelInnerW(): number {
    return this.reelVisualW;
  }

  private reelInnerH(): number {
    return this.reelVisualH;
  }

  private comboCellH(row = 0): number {
    return this.scaleX((this.layout.comboH[row] ?? this.layout.comboH[0]) - 4);
  }

  private comboRowStep(): number {
    if (this.layout.comboY.length < 2) return this.comboCellH(0) + this.scaleX(8);
    return this.scaleX(this.layout.comboY[1] - this.layout.comboY[0]);
  }

  private comboGridBottomY(): number {
    if (this.layout.profile !== 'portrait') {
      const last = this.layout.comboY.length - 1;
      return this.scalePosY(this.layout.comboY[last]) + this.comboOffsetY() + this.comboCellH(last) / 2;
    }
    return this.safeBottom - this.scaleX(28);
  }

  private comboGridTopY(): number {
    if (this.layout.profile !== 'portrait') {
      return this.scalePosY(this.layout.comboY[0]) + this.comboOffsetY() - this.comboCellH(0) / 2;
    }
    const last = this.layout.comboY.length - 1;
    return this.comboGridBottomY() - this.comboRowStep() * last - this.comboCellH(last);
  }

  private comboRowCenterY(row: number): number {
    if (this.layout.profile !== 'portrait') return this.scalePosY(this.layout.comboY[row]) + this.comboOffsetY();
    return this.comboGridTopY() + this.comboCellH(row) / 2 + this.comboRowStep() * row;
  }

  private resolveThemeVisual(): ThemeVisual {
    try {
      return THEME_VISUALS[getSelectedTheme()] ?? THEME_VISUALS.classic;
    } catch {
      return THEME_VISUALS.classic;
    }
  }

  private isCanvasRenderer(): boolean {
    return this.game.renderer.type === Phaser.CANVAS;
  }

  private applyCenterSpriteStyle(sprite: Phaser.GameObjects.Image | undefined): void {
    sprite?.setVisible(true).setAlpha(0.98).clearTint();
  }

  private getGhostTextureKey(textureKey: string): string {
    if (!this.isCanvasRenderer()) return textureKey;
    const ghostKey = `${textureKey}-ghost-dim`;
    if (this.textures.exists(ghostKey)) return ghostKey;
    if (!this.textures.exists(textureKey)) return textureKey;

    const source = this.textures.get(textureKey).getSourceImage() as CanvasImageSource & {
      width?: number;
      height?: number;
      naturalWidth?: number;
      naturalHeight?: number;
    };
    const width = Math.max(1, Math.round(source.naturalWidth || source.width || 1));
    const height = Math.max(1, Math.round(source.naturalHeight || source.height || 1));
    const dimTexture = this.textures.createCanvas(ghostKey, width, height);
    if (!dimTexture) return textureKey;

    const ctx = dimTexture.getContext();
    ctx.clearRect(0, 0, width, height);
    ctx.globalAlpha = 0.22;
    ctx.drawImage(source, 0, 0, width, height);
    ctx.globalAlpha = 1;
    dimTexture.refresh();
    return ghostKey;
  }

  private setGhostSpriteTexture(sprite: Phaser.GameObjects.Image | undefined, textureKey: string): void {
    sprite?.setTexture(this.getGhostTextureKey(textureKey));
  }

  private applyGhostSpriteStyle(sprite: Phaser.GameObjects.Image | undefined): void {
    if (!sprite) return;
    sprite.setVisible(true).setAlpha(this.isCanvasRenderer() ? 1 : 0.22).clearTint();
  }

  private pickLayout(width: number, height: number): GameLayout {
    const portraitThreshold = width * 1.02;
    const profile: GameLayoutProfile = height >= portraitThreshold ? 'portrait' : 'landscape';
    return GAME_LAYOUTS[profile];
  }

  private computeLayoutDerivedValues(): void {
    const comboPanelLeftX = this.layout.rightCellLeft.x - this.layout.rightCellLeft.w / 2 - this.layout.comboPanelPadX;
    const comboPanelRightX = this.layout.rightCellRight.x + this.layout.rightCellRight.w / 2 + this.layout.comboPanelPadX;
    this.comboPanelBgX = (comboPanelLeftX + comboPanelRightX) / 2;
    this.comboPanelBgW = comboPanelRightX - comboPanelLeftX;

    // We'll dynamically compute these in the build methods based on available space.
    const lastReelX = this.layout.reelX[this.layout.reelX.length - 1];
    const contentMaxX = Math.max(
      lastReelX + this.layout.reelW / 2,
      this.layout.topScoreBox.x + this.layout.topScoreBox.w / 2,
      comboPanelRightX,
    );
    this.gameRefXOffset = 0;
  }

  private makeResizeSignature(width: number, height: number): string {
    const insets = this.readSafeAreaInsets(width, height);
    return [
      `${Math.round(width)}x${Math.round(height)}`,
      height > width ? 'p' : 'l',
      Math.round(insets.top),
      Math.round(insets.right),
      Math.round(insets.bottom),
      Math.round(insets.left),
    ].join('-');
  }

  private resolveSafeArea(width: number, height: number): void {
    const insets = this.readSafeAreaInsets(width, height);
    this.safeLeft = insets.left;
    this.safeRight = insets.right;
    this.safeTop = insets.top;
    this.safeBottom = height - insets.bottom;
    this.safeW = Math.max(320, width - this.safeLeft - insets.right);
    this.safeH = Math.max(220, this.safeBottom - this.safeTop);
    this.safeCx = this.safeLeft + this.safeW / 2;
    this.safeCy = this.safeTop + this.safeH / 2;
  }

  private readSafeAreaInsets(width: number, height: number): { top: number; right: number; bottom: number; left: number } {
    if (typeof window === 'undefined') {
      return { top: 0, right: 0, bottom: 0, left: 0 };
    }

    const style = getComputedStyle(document.documentElement);
    // Game/camera dimensions are in canvas pixels (viewport CSS × devicePixelRatio).
    // CSS env(safe-area-inset-*) values are reported in CSS pixels, so multiply
    // by the same DPR factor we used for the canvas to keep the math consistent.
    const dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 3));
    const hardMax = Math.max(0, Math.min(width, height) * 0.32);
    const parseInset = (prop: string): number => {
      const raw = style.getPropertyValue(prop).trim();
      const parsed = Number.parseFloat(raw);
      if (!Number.isFinite(parsed)) return 0;
      return Math.max(0, Math.min(hardMax, parsed * dpr));
    };

    return {
      top: parseInset('--safe-area-inset-top'),
      right: parseInset('--safe-area-inset-right'),
      bottom: parseInset('--safe-area-inset-bottom'),
      left: parseInset('--safe-area-inset-left'),
    };
  }

  private getFrameBadgePosition(
    side: 'left' | 'right',
    width: number,
    height: number,
    frameInset = 14,
    frameGap = 10,
  ): { x: number; y: number } {
    const insetX = this.scaleX(frameInset);
    const gapY = this.scaleX(frameGap);
    const x = side === 'left'
      ? this.safeLeft + insetX + width / 2
      : this.safeLeft + this.safeW - insetX - width / 2;

    return {
      x,
      y: this.safeTop + gapY + height / 2,
    };
  }

  private handleResize(gameSize: Phaser.Structs.Size): void {
    const nextSignature = this.makeResizeSignature(gameSize.width, gameSize.height);
    if (nextSignature === this.resizeSignature) return;
    this.resizeSignature = nextSignature;

    this.resizeTimer?.destroy();
    this.resizeTimer = this.time.delayedCall(120, () => {
      this.scene.restart(this.roomState ? { room: this.roomState } : undefined);
    });
  }

  private setupForegroundSync(): void {
    if (typeof window === 'undefined' || this.sm.isLocalMode()) return;
    if (this.appForegroundHandler) return;

    this.appForegroundHandler = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        this.refreshBackgroundRoundNotification();
        return;
      }
      void roundNotificationManager.cancelNextRoundNotification();
      this.requestStateResync('app-foreground');
    };
    window.addEventListener('focus', this.appForegroundHandler);
    window.addEventListener('pageshow', this.appForegroundHandler);
    document.addEventListener('visibilitychange', this.appForegroundHandler);
  }

  private teardownForegroundSync(): void {
    if (!this.appForegroundHandler || typeof window === 'undefined') return;
    window.removeEventListener('focus', this.appForegroundHandler);
    window.removeEventListener('pageshow', this.appForegroundHandler);
    document.removeEventListener('visibilitychange', this.appForegroundHandler);
    this.appForegroundHandler = undefined;
  }

  private maybeHandleRoundTransition(previousRoom: RoomState | undefined): void {
    if (!previousRoom || !this.roomState) return;
    if (this.roomState.currentRound <= previousRoom.currentRound) return;
    if (this.roomState.status !== RoomStatus.PLAYING) return;

    // Falls ROUND_START verpasst wurde (z.B. App im Hintergrund), Runde lokal sauber zurücksetzen.
    this.resetRoundSpecialState();
    this.leftDrawUsed = false;
    this.rightDrawUsed = false;
    this.pendingRoundEndResult = null;
    this.destroyRoundEndPopup();
    this.clearLockVisuals();
    this.refreshBackgroundRoundNotification();
  }

  private isDocumentHidden(): boolean {
    return typeof document !== 'undefined' && document.visibilityState === 'hidden';
  }

  private refreshBackgroundRoundNotification(): void {
    if (this.sm.isLocalMode() || !this.roomState) {
      void roundNotificationManager.cancelNextRoundNotification();
      return;
    }

    if (!this.isDocumentHidden()) {
      void roundNotificationManager.cancelNextRoundNotification();
      return;
    }

    const room = this.roomState;
    if (room.currentRound >= room.totalRounds) {
      void roundNotificationManager.cancelNextRoundNotification();
      return;
    }

    if (room.status === RoomStatus.ROUND_END) {
      const nextRound = Math.min(room.totalRounds, room.currentRound + 1);
      const body = `Runde ${nextRound}/${room.totalRounds} hat begonnen.`;
      void roundNotificationManager.scheduleNextRoundNotification(Date.now() + ROUND_PAUSE_MS + 400, body);
      return;
    }

    if (room.status !== RoomStatus.PLAYING) {
      void roundNotificationManager.cancelNextRoundNotification();
      return;
    }

    const elapsed = Math.max(0, Date.now() - (room.roundStartTime || Date.now()));
    const fallbackLeft = Math.max(0, room.roundDuration - elapsed);
    const roundTimeLeft = Number.isFinite(room.roundTimeLeft)
      ? Math.max(0, room.roundTimeLeft)
      : fallbackLeft;
    const nextRound = Math.min(room.totalRounds, room.currentRound + 1);
    const body = `Runde ${nextRound}/${room.totalRounds} hat begonnen.`;
    void roundNotificationManager.scheduleNextRoundNotification(
      Date.now() + roundTimeLeft + ROUND_PAUSE_MS + 450,
      body,
    );
  }

  private notifyRoundStartIfBackground(): void {
    if (this.sm.isLocalMode() || !this.roomState) return;
    if (!this.isDocumentHidden()) {
      void roundNotificationManager.cancelNextRoundNotification();
      return;
    }

    const body = `Runde ${this.roomState.currentRound}/${this.roomState.totalRounds} hat begonnen.`;
    void roundNotificationManager.scheduleNextRoundNotification(Date.now() + 700, body);
  }

  private setupKeyboardControls(): void {
    const keyboard = this.input.keyboard;
    if (!keyboard) return;

    this.keydownHandler = (event: KeyboardEvent) => {
      if (event.repeat) return;
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }

      const isSpace = event.code === 'Space' || event.key === ' ' || event.key === 'Spacebar';
      if (isSpace) {
        event.preventDefault();
        this.handleSpaceDraw();
        return;
      }

      const reelIndex = this.keyToReelIndex(event.code, event.key);
      if (reelIndex === null) return;

      event.preventDefault();
      this.toggleLock(reelIndex);
    };

    keyboard.on('keydown', this.keydownHandler);
  }

  private removeKeyboardControls(): void {
    const keyboard = this.input.keyboard;
    if (keyboard && this.keydownHandler) {
      keyboard.off('keydown', this.keydownHandler);
    }
    this.keydownHandler = undefined;
  }

  private handleGameObjectDown(): void {
    playSfx(this, SFX_TAP_KEY, {
      volume: 0.18,
      rate: Phaser.Math.FloatBetween(0.97, 1.03),
    });
  }

  private keyToReelIndex(code: string, key: string): number | null {
    switch (code || key) {
      case 'Digit1':
      case 'Numpad1':
      case '1':
        return 0;
      case 'Digit2':
      case 'Numpad2':
      case '2':
        return 1;
      case 'Digit3':
      case 'Numpad3':
      case '3':
        return 2;
      case 'Digit4':
      case 'Numpad4':
      case '4':
        return 3;
      case 'Digit5':
      case 'Numpad5':
      case '5':
        return 4;
      default:
        return null;
    }
  }

  private handleSpaceDraw(): void {
    if (!this.myPlayer || this.myPlayer.status !== PlayerStatus.PLAYING || this.spinning) return;
    if (this.myPlayer.drawsLeft <= 0) return;

    // Keyboard order: first left draw, then right draw.
    if (!this.leftDrawUsed) {
      this.doDrawLeft();
      return;
    }

    if (!this.rightDrawUsed) {
      this.doDrawRight();
    }
  }

  private fontPx(v: number, min = 10): string {
    const base = Math.round(v * this.cabinetScale);
    const readabilityBoost = (v <= 22 ? 1 : 0) + (this.cabinetScale < 0.75 && v <= 24 ? 1 : 0);
    return `${Math.max(min, base + readabilityBoost)}px`;
  }

  private makeText(
    x: number,
    y: number,
    text: string | string[],
    style?: Phaser.Types.GameObjects.Text.TextStyle,
  ): Phaser.GameObjects.Text {
    // Canvas is already rendered at devicePixelRatio internal resolution
    // (see main.ts). Text inherits that resolution implicitly; calling
    // setResolution(dpr) on top double-scales the texture canvas and can
    // misrender on the Canvas renderer — leave the default resolution.
    return this.add.text(x, y, text, style);
  }

  private strokePx(scale = 2, min = 1): number {
    return Math.max(min, Math.round(this.cabinetScale * scale * 0.55));
  }

  private redrawBtnGfx(
    gfx: Phaser.GameObjects.Graphics,
    w: number, h: number, rad: number,
    fillColor: number, fillAlpha: number,
    strokeColor: number, strokeAlpha: number,
    lineWidth: number,
  ): void {
    gfx.clear();
    if (fillAlpha > 0) {
      gfx.fillStyle(fillColor, fillAlpha);
      gfx.fillRoundedRect(-w / 2, -h / 2, w, h, rad);
    }
    gfx.lineStyle(lineWidth, strokeColor, strokeAlpha);
    gfx.strokeRoundedRect(-w / 2, -h / 2, w, h, rad);
  }

  private drawPanelBox(
    box: { x: number; y: number; w: number; h: number },
    fill: number,
    stroke: number,
    alpha = 0.9,
  ): void {
    const bx = this.scalePos(box.x);
    const by = this.scalePosY(box.y);
    const bw = this.scaleX(box.w - 8);
    const bh = this.scaleX(box.h - 8);
    const rad = this.scaleX(6);
    const line = Math.max(1, Math.round(this.cabinetScale * 2));

    const g = this.add.graphics();
    g.fillStyle(fill, alpha);
    g.fillRoundedRect(bx - bw / 2, by - bh / 2, bw, bh, rad);
    g.lineStyle(line, stroke, 0.75);
    g.strokeRoundedRect(bx - bw / 2, by - bh / 2, bw, bh, rad);

    // Top bevel highlight
    this.add.rectangle(
      bx,
      this.scalePosY(box.y - box.h / 2 + 6),
      this.scaleX(box.w - 16),
      this.scaleX(6),
      0xffffff,
      0.08,
    ).setBlendMode(Phaser.BlendModes.ADD);
  }

  private drawMiniSymbolRow(
    xRef: number,
    yRef: number,
    symbols: GameSymbol[],
    size: number,
    alpha: number,
  ): void {
    const gap = this.scaleX(28);
    const totalW = (symbols.length - 1) * gap;
    const startX = this.scalePos(xRef) - totalW / 2;
    const y = this.scalePosY(yRef);

    symbols.forEach((sym, i) => {
      const cfg = SYM[sym];
      const x = startX + i * gap;
      if (this.textures.exists(cfg.textureKey)) {
        this.add.image(x, y, cfg.textureKey)
          .setDisplaySize(size, size)
          .setAlpha(alpha)
          .setTint(0xffffff);
      } else {
        this.makeText(x, y, cfg.char, {
          fontFamily: 'Arial Black, Impact, sans-serif',
          fontSize: this.fontPx(16, 8),
          color: '#' + cfg.color.toString(16).padStart(6, '0'),
          stroke: '#2b0f43',
          strokeThickness: Math.max(1, Math.round(this.cabinetScale * 2)),
        }).setOrigin(0.5).setAlpha(alpha);
      }
    });
  }

  private startAmbientFx(): void {
    const t = this.theme;
    // Dual-color sweeps crossing in opposite directions
    const sweepColors = [t.accent, t.accent2];
    sweepColors.forEach((color, si) => {
      const sweepWidth = this.scaleX(this.layout.profile === 'portrait' ? 46 : 54);
      const sweepHeight = this.mainH - this.scaleX(this.layout.profile === 'portrait' ? 240 : 112);
      const startX = si === 0
        ? this.cabinetLeft - this.scaleX(80)
        : this.cabinetLeft + this.mainW + this.scaleX(80);
      const endX = si === 0
        ? this.cabinetLeft + this.mainW + this.scaleX(80)
        : this.cabinetLeft - this.scaleX(80);
      const sweep = this.add.rectangle(
        startX, this.centerY,
        sweepWidth, sweepHeight,
        color, si === 0 ? 0.032 : 0.024,
      ).setAngle(si === 0 ? -10 : 10).setBlendMode(Phaser.BlendModes.ADD);

      this.ambientFxTweens.push(this.tweens.add({
        targets: sweep,
        x: endX,
        duration: 3400 + si * 700,
        ease: 'Sine.easeInOut',
        repeat: -1,
        repeatDelay: 1500 + si * 450,
        onRepeat: () => sweep.setX(startX),
      }));
    });

    // Vivid sparkle field
    const sparkleCount = this.layout.profile === 'portrait' ? 28 : 20;
    const sparkleColors = [t.accent, t.accent2, t.stroke, t.accent3, t.good, t.strokeSoft];
    for (let i = 0; i < sparkleCount; i++) {
      const x = this.cabinetLeft + this.scaleX(24) + Math.random() * (this.mainW - this.scaleX(48));
      const y = this.cabinetTop + this.scaleX(24) + Math.random() * (this.mainH - this.scaleX(48));
      const color = sparkleColors[i % sparkleColors.length];
      const sparkle = this.add.circle(x, y, this.scaleX(1 + Math.random() * 2.8), color, 0.06 + Math.random() * 0.16)
        .setBlendMode(Phaser.BlendModes.ADD);

      this.ambientFxTweens.push(this.tweens.add({
        targets: sparkle,
        alpha: { from: 0.03, to: 0.32 },
        duration: 500 + Math.random() * 1200,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
        delay: Math.random() * 1000,
      }));

      const driftX = this.scaleX((Math.random() - 0.5) * 52);
      const driftY = this.scaleX((Math.random() - 0.5) * 40);
      this.ambientFxTweens.push(this.tweens.add({
        targets: sparkle,
        x: x + driftX,
        y: y + driftY,
        duration: 1800 + Math.random() * 2400,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
        delay: Math.random() * 800,
      }));
    }
  }

  private stopAmbientFx(): void {
    this.ambientFxTweens.forEach((tw) => tw.remove());
    this.ambientFxTweens = [];
    this.comboGlowTweens.forEach((tw) => tw?.remove());
    this.comboGlowTweens = [];
  }

  private ensureComboGlow(index: number): void {
    const glow = this.comboPulseGlows[index];
    if (!glow) return;
    glow.setVisible(true).setAlpha(0.15);

    if (this.comboGlowTweens[index]) return;
    this.comboGlowTweens[index] = this.tweens.add({
      targets: glow,
      alpha: { from: 0.08, to: 0.4 },
      scaleX: { from: 1.0, to: 1.03 },
      scaleY: { from: 1.0, to: 1.03 },
      duration: 450,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private stopComboGlow(index: number): void {
    const tw = this.comboGlowTweens[index];
    if (tw) tw.remove();
    this.comboGlowTweens[index] = null;

    const glow = this.comboPulseGlows[index];
    if (glow) {
      glow.setVisible(false).setAlpha(0).setScale(1);
    }
  }

  private emitSparkBurst(x: number, y: number, color = 0x00ffee, count = 14): void {
    const t = this.theme;
    const burstColors = [color, t.accent2, t.stroke, t.accent3];
    for (let i = 0; i < count; i++) {
      const c = i < count * 0.6 ? color : burstColors[Math.floor(Math.random() * burstColors.length)];
      const spark = this.add.circle(x, y, this.scaleX(1.4 + Math.random() * 3), c, 1)
        .setBlendMode(Phaser.BlendModes.ADD);
      const angle = Math.random() * Math.PI * 2;
      const dist = this.scaleX(24 + Math.random() * 42);
      const targetX = x + Math.cos(angle) * dist;
      const targetY = y + Math.sin(angle) * dist;

      this.tweens.add({
        targets: spark,
        x: targetX,
        y: targetY,
        alpha: 0,
        scale: { from: 1, to: 0.2 },
        duration: 280 + Math.random() * 220,
        ease: 'Cubic.easeOut',
        onComplete: () => spark.destroy(),
      });

      // Trail particle
      if (i % 3 === 0) {
        const trail = this.add.circle(x, y, this.scaleX(0.8), c, 0.6)
          .setBlendMode(Phaser.BlendModes.ADD);
        this.tweens.add({
          targets: trail,
          x: x + Math.cos(angle) * dist * 0.6,
          y: y + Math.sin(angle) * dist * 0.6,
          alpha: 0,
          duration: 200 + Math.random() * 120,
          ease: 'Quad.easeOut',
          onComplete: () => trail.destroy(),
        });
      }
    }
  }

  private playRoundStartImpact(): void {
    const t = this.theme;
    const accent = Phaser.Display.Color.IntegerToColor(t.accent);
    // Dual-color flash, theme-aware
    this.cameras.main.flash(140, accent.red, accent.green, accent.blue, true);

    // Expanding cyan ring
    const ring = this.add.circle(
      this.centerX, this.centerY,
      this.scaleX(40), t.accent, 0,
    ).setStrokeStyle(this.scaleX(3), t.accent, 0.8)
      .setBlendMode(Phaser.BlendModes.ADD);

    this.tweens.add({
      targets: ring,
      scaleX: 8, scaleY: 8,
      alpha: 0,
      duration: 500,
      ease: 'Cubic.easeOut',
      onComplete: () => ring.destroy(),
    });

    // Central glow burst
    const glow = this.add.rectangle(
      this.centerX, this.centerY,
      this.mainW - this.scaleX(20), this.mainH - this.scaleX(20),
      t.accent, 0.18,
    ).setBlendMode(Phaser.BlendModes.ADD);

    this.tweens.add({
      targets: glow,
      alpha: 0,
      scaleX: 1.05, scaleY: 1.05,
      duration: 380,
      ease: 'Sine.easeOut',
      onComplete: () => glow.destroy(),
    });

    // Secondary magenta pulse
    this.time.delayedCall(80, () => {
      const pulse = this.add.rectangle(
        this.centerX, this.centerY,
        this.mainW - this.scaleX(32), this.mainH - this.scaleX(32),
        t.accent2, 0.12,
      ).setBlendMode(Phaser.BlendModes.ADD);
      this.tweens.add({
        targets: pulse,
        alpha: 0,
        scaleX: 1.03, scaleY: 1.03,
        duration: 300,
        ease: 'Sine.easeOut',
        onComplete: () => pulse.destroy(),
      });
    });

    // Corner spark bursts
    const corners = [
      [this.cabinetLeft + this.scaleX(30), this.cabinetTop + this.scaleX(30)],
      [this.cabinetLeft + this.mainW - this.scaleX(30), this.cabinetTop + this.scaleX(30)],
      [this.cabinetLeft + this.scaleX(30), this.cabinetTop + this.mainH - this.scaleX(30)],
      [this.cabinetLeft + this.mainW - this.scaleX(30), this.cabinetTop + this.mainH - this.scaleX(30)],
    ];
    corners.forEach(([cx, cy], i) => {
      this.time.delayedCall(i * 40, () => {
        this.emitSparkBurst(cx, cy, t.accent, 6);
      });
    });
  }

  private pulseDrawButton(isLeft: boolean): void {
    const btnBg = isLeft ? this.drawBtnBg : this.drawBtnAltBg;
    const btnText = isLeft ? this.drawBtnText : this.drawBtnAltText;
    const btnGfx = isLeft ? this.drawBtnGfxL : this.drawBtnGfxR;
    const halo = isLeft ? this.drawBtnHaloL : this.drawBtnHaloR;
    const x = btnBg.x;
    const y = btnBg.y;

    this.tweens.killTweensOf([btnBg, btnText, btnGfx, halo]);
    this.tweens.add({
      targets: [btnBg, btnText, btnGfx],
      scaleX: 1.06,
      scaleY: 1.06,
      duration: 95,
      yoyo: true,
      ease: 'Back.easeOut',
      onComplete: () => {
        btnBg.setScale(1);
        btnText.setScale(1);
        btnGfx.setScale(1);
      },
    });
    this.tweens.add({
      targets: halo,
      alpha: { from: 0.4, to: 0.08 },
      scaleX: { from: 1.08, to: 1.0 },
      scaleY: { from: 1.08, to: 1.0 },
      duration: 300,
      ease: 'Quad.easeOut',
    });
    this.emitSparkBurst(x, y, this.theme.accent, 14);
  }

  private emitReelLockBurst(index: number, locked: boolean): void {
    const x = this.reelCenterX(index);
    const y = this.reelY;
    const t = this.theme;
    const pulseColor = locked ? t.accent3 : t.accent;
    const pulse = this.add.rectangle(
      x, y,
      this.reelInnerW() + this.scaleX(24),
      this.reelInnerH() + this.scaleX(24),
      pulseColor, 0.4,
    ).setBlendMode(Phaser.BlendModes.ADD);

    this.tweens.add({
      targets: pulse,
      alpha: 0,
      scaleX: 1.12,
      scaleY: 1.12,
      duration: 280,
      ease: 'Cubic.easeOut',
      onComplete: () => pulse.destroy(),
    });
    this.emitSparkBurst(x, y, pulseColor, 10);
  }

  private buildCabinetBackground(w: number, h: number): void {
    const t = this.theme;
    const sw = (v: number) => this.scaleX(v);
    const sx = (v: number) => this.scalePos(v);

    // Full-screen deep background — no outer frame, the screen IS the machine
    const bgGfx = this.add.graphics();
    bgGfx.fillGradientStyle(t.bgTop, t.bgTop, t.bgBottom, t.bgBottom, 1);
    bgGfx.fillRect(0, 0, w, h);

    // Soft spotlight behind reel area
    const spotlightX = this.scalePos(this.layout.leftAreaAnchorX);
    const spotlightY = this.reelY;
    const spotlightR = Math.max(this.mainW * 0.44, this.mainH * 0.8);
    const spotlight = this.add.circle(spotlightX, spotlightY, spotlightR, t.accent, 0.055)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.ambientFxTweens.push(this.tweens.add({
      targets: spotlight,
      alpha: { from: 0.03, to: 0.08 },
      duration: 2800, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    }));

    const spotB = this.add.circle(spotlightX, spotlightY, spotlightR * 0.6, t.accent2, 0.035)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.ambientFxTweens.push(this.tweens.add({
      targets: spotB,
      alpha: { from: 0.02, to: 0.065 },
      duration: 3400, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    }));

    // Combo panel strip — darker panel behind the combo cells
    const comboStripX = sx(this.comboPanelBgX) + this.comboOffsetX();
    const comboStripW = sw(this.comboPanelBgW + 6);
    const comboStripTop = this.comboGridTopY() - this.scaleX(10);
    const comboStripBottom = this.comboGridBottomY() + this.scaleX(10);
    const comboStripH = comboStripBottom - comboStripTop;
    const rpGfx = this.add.graphics();
    rpGfx.fillGradientStyle(t.panelA, t.panelA, t.panelB, t.panelB, 0.82);
    rpGfx.fillRoundedRect(
      comboStripX - comboStripW / 2, comboStripTop,
      comboStripW, comboStripH, sw(6),
    );
    // Subtle neon left border on the combo strip
    rpGfx.lineStyle(Math.max(2, Math.round(this.cabinetScale * 2)), t.stroke, 0.55);
    rpGfx.lineBetween(
      comboStripX - comboStripW / 2 + sw(1),
      comboStripTop + sw(4),
      comboStripX - comboStripW / 2 + sw(1),
      comboStripTop + comboStripH - sw(4),
    );
    // Outer rounded stroke
    rpGfx.lineStyle(Math.max(1, Math.round(this.cabinetScale)), t.strokeSoft, 0.35);
    rpGfx.strokeRoundedRect(
      comboStripX - comboStripW / 2, comboStripTop,
      comboStripW, comboStripH, sw(6),
    );

    // Title label: PICKITUP — small, elegant, floats at the very top center
    if (this.layout.profile === 'landscape') {
      const titleX = this.scalePos(this.layout.leftAreaAnchorX);
      const titleY = this.cabinetTop + sw(14);
      this.makeText(titleX, titleY, 'PICKITUP', {
        fontFamily: 'Arial Black, Inter, sans-serif',
        fontSize: this.fontPx(13, 9),
        color: hexColor(t.strokeSoft),
        fontStyle: '900',
        letterSpacing: 3,
      }).setOrigin(0.5).setAlpha(0.38);
    }
  }

  private buildMainFrame(): void {
    const t = this.theme;

    // No outer frame — removed entirely. Ambient orbs only.

    // Ambient floating light orbs with color cycling
    const orbData = [
      { rx: 300, ry: 160, r: 65, c: t.accent, a: 0.05 },
      { rx: 850, ry: 340, r: 55, c: t.accent2, a: 0.04 },
      { rx: 550, ry: 100, r: 45, c: t.stroke, a: 0.04 },
      { rx: 180, ry: 360, r: 50, c: t.accent, a: 0.035 },
      { rx: 750, ry: 140, r: 40, c: t.accent3, a: 0.035 },
      { rx: 450, ry: 380, r: 35, c: t.good, a: 0.03 },
    ];
    orbData.forEach(({ rx, ry, r, c, a }) => {
      const orb = this.add.circle(this.scalePos(rx), this.scalePosY(ry), this.scaleX(r), c, a)
        .setBlendMode(Phaser.BlendModes.ADD);
      this.ambientFxTweens.push(this.tweens.add({
        targets: orb,
        alpha: { from: a * 0.75, to: a * 1.35 },
        duration: 2200 + Math.random() * 1800,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      }));
      this.ambientFxTweens.push(this.tweens.add({
        targets: orb,
        y: orb.y + this.scaleX((Math.random() - 0.5) * 18),
        duration: 3000 + Math.random() * 2000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      }));
    });
  }

  /* ==================================================================
   *  TOP BAR
   * ================================================================== */

  private buildTopBar(_w: number): void {
    const t = this.theme;
    const portrait = this.layout.profile === 'portrait';
    const sw = (v: number) => this.scaleX(v);
    const headerPadX = this.scaleX(8);
    const headerPadY = this.scaleX(8);
    const colGap = this.scaleX(portrait ? 8 : 10);
    const rowGap = this.scaleX(10);
    const rowH = this.scaleX(portrait ? 42 : 40);
    const buttonW = this.scaleX(portrait ? 132 : 148);
    const scoreW = this.scaleX(portrait ? 132 : 148);
    const buttonH = rowH;
    const scoreH = rowH;
    const headerH = rowH * 2 + rowGap;
    const headerW = this.safeW - headerPadX * 2;
    const headerLeft = this.safeLeft + headerPadX;
    const headerRight = headerLeft + headerW;
    const leftColW = buttonW;
    const rightColW = scoreW;
    const timerW = Math.max(this.scaleX(portrait ? 180 : 280), headerW - leftColW - rightColW - colGap * 2);
    const timerLeft = headerLeft + leftColW + colGap;
    const timerRight = timerLeft + timerW;
    const timerX = timerLeft + timerW / 2;
    const topY = headerPadY + this.safeTop;
    const rowTopY = topY + rowH / 2;
    const rowBottomY = topY + rowH + rowGap + rowH / 2;
    const timerY = topY + headerH / 2;
    const cellRad = this.scaleX(8);

    this.headerLeft = headerLeft;
    this.headerRight = headerRight;
    this.headerTop = topY;
    this.headerBottom = topY + headerH;
    this.headerRowTopY = rowTopY;
    this.headerRowBottomY = rowBottomY;
    this.headerTimerX = timerX;
    this.headerTimerW = timerW;
    this.headerCellW = leftColW;
    this.headerCellH = rowH;

    const drawCell = (x: number, y: number, w: number, h: number, fill: number, stroke: number, strokeAlpha = 0.72) => {
      const gfx = this.add.graphics();
      gfx.fillGradientStyle(fill, fill, t.panelB, t.panelB, 0.96);
      gfx.fillRoundedRect(x - w / 2, y - h / 2, w, h, cellRad);
      gfx.lineStyle(Math.max(1, Math.round(this.cabinetScale * 2)), stroke, strokeAlpha);
      gfx.strokeRoundedRect(x - w / 2, y - h / 2, w, h, cellRad);
      const shine = this.add.graphics();
      shine.fillGradientStyle(0xffffff, 0xffffff, 0, 0, 0.12, 0.12, 0, 0);
      shine.fillRoundedRect(x - w / 2 + sw(1), y - h / 2 + sw(1), w - sw(2), h * 0.42, { tl: cellRad, tr: cellRad, bl: 0, br: 0 });
    };

    const panelGfx = this.add.graphics();
    panelGfx.fillGradientStyle(t.panelA, t.panelA, t.panelB, t.panelB, 0.72);
    panelGfx.fillRoundedRect(headerLeft, topY, headerW, headerH, cellRad);
    panelGfx.lineStyle(Math.max(1, Math.round(this.cabinetScale * 2)), t.strokeSoft, 0.32);
    panelGfx.strokeRoundedRect(headerLeft, topY, headerW, headerH, cellRad);

    drawCell(headerLeft + leftColW / 2, rowTopY, leftColW, rowH, t.panelA, t.stroke);
    drawCell(headerLeft + leftColW / 2, rowBottomY, leftColW, rowH, t.panelA, t.stroke);

    const timerBg = this.add.graphics();
    timerBg.fillGradientStyle(t.innerA, t.innerA, t.innerB, t.innerB, 0.96);
    timerBg.fillRoundedRect(timerLeft, topY, timerW, headerH, cellRad);
    timerBg.lineStyle(Math.max(1, Math.round(this.cabinetScale * 2)), t.stroke, 0.42);
    timerBg.strokeRoundedRect(timerLeft, topY, timerW, headerH, cellRad);

    drawCell(headerRight - rightColW / 2, rowTopY, rightColW, rowH, t.panelA, t.good, 0.68);
    drawCell(headerRight - rightColW / 2, rowBottomY, rightColW, rowH, t.panelA, t.good, 0.68);

    this.timerBarFullW = timerW - sw(10);
    const barH = Math.max(sw(18), headerH - sw(12));
    const barY = timerY;
    const barX = timerX;
    this.timerBarBg = this.add.rectangle(barX, barY, timerW - sw(8), barH, 0x000000, 0);

    // Lightweight timer fill: one solid bar (blue -> red based on remaining time)
    this.timerBar = this.add.rectangle(barX - this.timerBarFullW / 2, barY, this.timerBarFullW, barH, 0x2f8cff, 1).setOrigin(0, 0.5);
    this.timerShine?.destroy();
    this.timerShine = undefined;

    this.roundText = this.makeText(headerLeft + leftColW / 2, rowBottomY, 'Round 0/5', {
      fontFamily: 'Inter, Arial, sans-serif',
      fontSize: this.fontPx(portrait ? 13 : 12, 10),
      color: t.text,
      stroke: hexColor(t.bgBottom),
      strokeThickness: this.strokePx(1.8, 1),
      fontStyle: '800',
      align: 'center',
    }).setOrigin(0.5);

    this.scoreText = this.makeText(headerRight - rightColW / 2, rowTopY, '0', {
      fontFamily: 'Inter, Arial, sans-serif',
      fontSize: this.fontPx(portrait ? 15 : 14, 10),
      color: hexColor(t.good),
      stroke: hexColor(t.bgTop),
      strokeThickness: this.strokePx(1.8, 1),
      fontStyle: '800',
      align: 'center',
    }).setOrigin(0.5);

    this.roundScoreText = this.makeText(headerRight - rightColW / 2, rowBottomY, '0', {
      fontFamily: 'Inter, Arial, sans-serif',
      fontSize: this.fontPx(portrait ? 13 : 12, 9),
      color: hexColor(t.accent3),
      stroke: hexColor(t.bgTop),
      strokeThickness: this.strokePx(1.2, 1),
      fontStyle: '700',
      align: 'center',
    }).setOrigin(0.5);
  }

  private buildOnlineCounter(): void {
    const t = this.theme;
    const baseW = this.scaleX(148);
    const baseH = this.scaleX(26);
    const { x: anchorX, y: anchorY } = this.getFrameBadgePosition('right', baseW, baseH, 14, 10);
    const fontSize = this.fontPx(10, 9);
    const stroke = this.strokePx(1.1, 1);

    this.onlineCounterBg = this.add.rectangle(
      anchorX,
      anchorY,
      baseW,
      baseH,
      t.panelB,
      0.9,
    )
      .setOrigin(0.5)
      .setStrokeStyle(Math.max(1, Math.round(this.cabinetScale * 1.5)), t.accent, 0.68)
      .setDepth(2250);

    this.onlineCounterText = this.makeText(anchorX, anchorY, '', {
      fontFamily: 'Inter, Arial, sans-serif',
      fontSize,
      color: hexColor(t.accent),
      stroke: hexColor(t.bgTop),
      strokeThickness: stroke,
      fontStyle: '700',
      align: 'center',
    })
      .setOrigin(0.5)
      .setDepth(2251);

    this.renderOnlineCounter();
  }

  private onOnlineStats(stats: OnlineStats): void {
    this.onlineStats = stats;
    this.renderOnlineCounter();
  }

  private renderOnlineCounter(): void {
    if (!this.onlineCounterBg || !this.onlineCounterText) return;

    // Offline-Einzelspieler blendet den Online-Counter aus.
    if (this.sm?.isLocalMode()) {
      this.onlineCounterBg.setVisible(false);
      this.onlineCounterText.setVisible(false);
      return;
    }

    const padX = this.scaleX(10);
    const padY = this.scaleX(5);

    const onlineTotal = this.onlineStats?.onlineTotal;
    const content = Number.isFinite(onlineTotal)
      ? `Online: ${onlineTotal}`
      : 'Online: 0';

    this.onlineCounterText.setText(content).setVisible(true);
    const badgeW = this.onlineCounterText.width + padX * 2;
    const badgeH = this.onlineCounterText.height + padY * 2;
    const anchorX = this.safeLeft + this.safeW - this.scaleX(8) - badgeW / 2;
    const anchorY = this.headerBottom + this.scaleX(8) + badgeH / 2;

    this.onlineCounterText
      .setPosition(anchorX, anchorY);

    this.onlineCounterBg
      .setVisible(true)
      .setPosition(anchorX, anchorY)
      .setSize(badgeW, badgeH);
  }

  private buildSymbolStrip(): void {
    const t = this.theme;
    const sw = (v: number) => this.scaleX(v);

    // Minimal PICKITUP watermark behind the reels (very subtle)
    const motif = this.theme.id === 'royal' ? '♛'
      : this.theme.id === 'forest' ? '♣'
        : this.theme.id === 'midnight' ? '☾'
          : this.theme.id === 'candy' ? '✦'
            : this.theme.id === 'neon' ? '◆'
              : '★';
    const wmarkX = this.scalePos(this.layout.leftAreaAnchorX);
    const wmarkY = this.reelY;
    this.makeText(wmarkX, wmarkY, motif, {
      fontFamily: 'Arial Black, Impact, sans-serif',
      fontSize: this.fontPx(this.layout.profile === 'portrait' ? 88 : 110, 36),
      color: hexColor(t.accent),
      fontStyle: '900',
    }).setOrigin(0.5).setAlpha(0.04).setBlendMode(Phaser.BlendModes.ADD).setAngle(-8);

    // Thin separator line between reels bottom and draw buttons
    if (this.layout.profile === 'portrait') {
      const drawH = this.scaleX(this.layout.drawLeft.h - 8);
      const sepY = (this.reelY + this.reelInnerH() / 2 + this.flexDrawY - drawH / 2) / 2;
      const sepL = this.safeLeft + this.scaleX(46);
      const sepR = this.safeLeft + this.safeW - this.scaleX(46);
      const railGfx = this.add.graphics();
      railGfx.lineStyle(Math.max(1, Math.round(this.cabinetScale)), t.accent, 0.22);
      railGfx.lineBetween(sepL, sepY, sepR, sepY);
    }

  }

  /* ==================================================================
   *  REELS – centered, 5 slots
   * ================================================================== */

  private buildReels(): void {
    const t = this.theme;
    const innerW = this.reelInnerW();
    const innerH = this.reelInnerH();
    const fontSize = this.fontPx(88, 28);
    const ghostOffset = innerH / 3; // must match animation slotH = innerH / 3
    const symbolSize = Math.min(innerW * 0.88, innerH * 0.36);
    const reelRad = this.scaleX(8);

    for (let i = 0; i < REEL_COUNT; i++) {
      const x = this.reelCenterX(i);
      const y = this.reelY;

      // ── Outer glow halo (pulsing) ───────────────────────────────────
      const aura = this.add.rectangle(
        x, y,
        innerW + this.scaleX(14),
        innerH + this.scaleX(14),
        t.accent, 0.06,
      ).setBlendMode(Phaser.BlendModes.ADD);
      this.reelAuras.push(aura);
      this.ambientFxTweens.push(this.tweens.add({
        targets: aura,
        alpha: { from: 0.02, to: 0.1 },
        scaleX: { from: 0.99, to: 1.03 },
        scaleY: { from: 0.99, to: 1.03 },
        duration: 1000 + i * 130,
        yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      }));

      // ── Glow border overlay (used when reel is locked) ──────────────
      const glow = this.add.rectangle(x, y, innerW + this.scaleX(8), innerH + this.scaleX(8), t.accent2, 0)
        .setOrigin(0.5);
      this.reelGlows.push(glow);

      // ── Reel body: deep dark fill + subtle radial depth ─────────────
      const bodyGfx = this.add.graphics();
      // Base solid very dark fill
      bodyGfx.fillStyle(t.innerB, 1);
      bodyGfx.fillRoundedRect(x - innerW / 2, y - innerH / 2, innerW, innerH, reelRad);
      // Gradient overlay (slightly lighter at top-center for depth)
      bodyGfx.fillGradientStyle(t.innerA, t.innerA, t.bgTop, t.bgTop, 0.85, 0.85, 1, 1);
      bodyGfx.fillRoundedRect(x - innerW / 2, y - innerH / 2, innerW, innerH, reelRad);

      // ── Neon border: double-line glow effect ─────────────────────────
      // Outer soft glow ring
      const strokeGfxOuter = this.add.graphics();
      strokeGfxOuter.lineStyle(Math.max(3, Math.round(this.cabinetScale * 6)), t.stroke, 0.22);
      strokeGfxOuter.strokeRoundedRect(x - innerW / 2 - this.scaleX(2), y - innerH / 2 - this.scaleX(2), innerW + this.scaleX(4), innerH + this.scaleX(4), reelRad + this.scaleX(2));
      strokeGfxOuter.setBlendMode(Phaser.BlendModes.ADD);
      // Inner crisp border
      const strokeGfxInner = this.add.graphics();
      strokeGfxInner.lineStyle(Math.max(2, Math.round(this.cabinetScale * 2.5)), t.stroke, 0.85);
      strokeGfxInner.strokeRoundedRect(x - innerW / 2, y - innerH / 2, innerW, innerH, reelRad);

      // Invisible hit-area + bg rect for stroke update
      const bg = this.add.rectangle(x, y, innerW, innerH, 0x000000, INPUT_HIT_ALPHA);
      this.reelBgs.push(bg);

      // ── Top gradient fade (symbols fade into darkness above) ─────────
      const fadeTopGfx = this.add.graphics();
      fadeTopGfx.fillGradientStyle(t.bgTop, t.bgTop, 0, 0, 0.92, 0.92, 0, 0);
      fadeTopGfx.fillRect(x - innerW / 2 + 1, y - innerH / 2 + 1, innerW - 2, innerH * 0.26);
      fadeTopGfx.setBlendMode(Phaser.BlendModes.NORMAL);

      // ── Bottom gradient fade ─────────────────────────────────────────
      const fadeBotGfx = this.add.graphics();
      fadeBotGfx.fillGradientStyle(0, 0, t.bgTop, t.bgTop, 0, 0, 0.92, 0.92);
      fadeBotGfx.fillRect(x - innerW / 2 + 1, y + innerH / 2 - innerH * 0.26, innerW - 2, innerH * 0.26);
      fadeBotGfx.setBlendMode(Phaser.BlendModes.NORMAL);

      // ── Center highlight line (separator — shows active symbol slot) ─
      const sepGfx = this.add.graphics();
      sepGfx.lineStyle(Math.max(1, Math.round(this.cabinetScale)), t.stroke, 0.18);
      sepGfx.lineBetween(x - innerW / 2 + this.scaleX(4), y - innerH / 3, x + innerW / 2 - this.scaleX(4), y - innerH / 3);
      sepGfx.lineBetween(x - innerW / 2 + this.scaleX(4), y + innerH / 3, x + innerW / 2 - this.scaleX(4), y + innerH / 3);

      // ── Neon top cap bar (accent color strip at top of each reel) ────
      const topBarGfx = this.add.graphics();
      topBarGfx.fillGradientStyle(t.accent, t.accent2, t.accent, t.accent2, 0.88, 0.88, 0.88, 0.88);
      topBarGfx.fillRoundedRect(x - innerW / 2, y - innerH / 2 - this.scaleX(4), innerW, this.scaleX(5), { tl: reelRad, tr: reelRad, bl: 0, br: 0 });
      topBarGfx.setBlendMode(Phaser.BlendModes.ADD);

      const topGhost = this.makeText(x, y - ghostOffset, '?', {
        fontFamily: 'Arial Black, Impact, sans-serif',
        fontSize,
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: Math.max(2, Math.round(this.cabinetScale * 4)),
      }).setOrigin(0.5).setAlpha(0.26);
      this.reelGhostTopTexts.push(topGhost);

      const topGhostSprite = this.add.image(x, y - ghostOffset, SYM.STAR.textureKey)
        .setDisplaySize(symbolSize, symbolSize)
        .setVisible(false);
      this.setGhostSpriteTexture(topGhostSprite, SYM.STAR.textureKey);
      this.applyGhostSpriteStyle(topGhostSprite);
      topGhostSprite.setVisible(false);
      this.reelGhostTopSprites.push(topGhostSprite);

      const txt = this.makeText(x, y, '?', {
        fontFamily: 'Arial Black, Impact, sans-serif',
        fontSize,
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: Math.max(2, Math.round(this.cabinetScale * 4)),
      }).setOrigin(0.5);
      this.reelTexts.push(txt);

      const sprite = this.add.image(x, y, SYM.STAR.textureKey)
        .setDisplaySize(symbolSize, symbolSize)
        .setVisible(false);
      this.reelSprites.push(sprite);

      const bottomGhost = this.makeText(x, y + ghostOffset, '?', {
        fontFamily: 'Arial Black, Impact, sans-serif',
        fontSize,
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: Math.max(2, Math.round(this.cabinetScale * 4)),
      }).setOrigin(0.5).setAlpha(0.26);
      this.reelGhostBottomTexts.push(bottomGhost);

      const bottomGhostSprite = this.add.image(x, y + ghostOffset, SYM.STAR.textureKey)
        .setDisplaySize(symbolSize, symbolSize)
        .setVisible(false);
      this.setGhostSpriteTexture(bottomGhostSprite, SYM.STAR.textureKey);
      this.applyGhostSpriteStyle(bottomGhostSprite);
      bottomGhostSprite.setVisible(false);
      this.reelGhostBottomSprites.push(bottomGhostSprite);

      const hasSymbolTextures = this.textures.exists(SYM.STAR.textureKey);
      if (hasSymbolTextures) {
        topGhost.setAlpha(0);
        txt.setAlpha(0);
        bottomGhost.setAlpha(0);
        topGhostSprite.setVisible(true);
        sprite.setVisible(true);
        bottomGhostSprite.setVisible(true);
      }

      // Clip reel contents to the reel boundary
      const maskGfx = this.make.graphics({ x: 0, y: 0 });
      maskGfx.fillStyle(0xffffff);
      maskGfx.fillRect(x - innerW / 2, y - innerH / 2, innerW, innerH);
      const reelMask = maskGfx.createGeometryMask();
      [topGhost, txt, bottomGhost].forEach(t => t.setMask(reelMask));
      [topGhostSprite, sprite, bottomGhostSprite].forEach(s => s.setMask(reelMask));

      const lockIcon = this.makeText(x, y + innerH / 2 - this.scaleX(10), '', {
        fontFamily: 'Inter, Arial, sans-serif',
        fontSize: this.fontPx(11, 9),
        color: hexColor(t.accent3),
        stroke: hexColor(t.bgBottom),
        strokeThickness: this.strokePx(1.1, 1),
        fontStyle: '800',
      }).setOrigin(0.5);
      this.lockIcons.push(lockIcon);

      const hitArea = this.add.rectangle(x, y, innerW, innerH, 0x000000, INPUT_HIT_ALPHA)
        .setDepth(2100)
        .setInteractive({ useHandCursor: true })
        .on('pointerdown', () => this.toggleLock(i));
      this.reelHitAreas.push(hitArea);
    }
  }

  /* ==================================================================
   *  DRAW BUTTON
   * ================================================================== */

  private buildDrawButton(): void {
    const t = this.theme;
    const leftX = this.scalePos(this.layout.drawLeft.x);
    const leftY = this.flexDrawY;
    const rightX = this.scalePos(this.layout.drawRight.x);
    const rightY = this.flexDrawY;
    const btnW = this.scaleX(this.layout.drawLeft.w - 8);
    const btnH = this.scaleX(this.layout.drawLeft.h - 8);
    const rad = this.scaleX(10);
    const line = Math.max(1, Math.round(this.cabinetScale * 2));

    this.drawBtnGeom = { w: btnW, h: btnH, rad };

    // Outer glow halos (pulsing neon cyan when active)
    this.drawBtnHaloL = this.add.rectangle(leftX, leftY, btnW + this.scaleX(18), btnH + this.scaleX(14), t.accent, 0.16)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.drawBtnHaloR = this.add.rectangle(rightX, rightY, btnW + this.scaleX(18), btnH + this.scaleX(14), t.accent, 0.16)
      .setBlendMode(Phaser.BlendModes.ADD);

    // Pulse animation on halos
    [this.drawBtnHaloL, this.drawBtnHaloR].forEach(halo => {
      this.tweens.add({
        targets: halo,
        alpha: { from: 0.06, to: 0.25 },
        scaleX: { from: 1.0, to: 1.04 },
        scaleY: { from: 1.0, to: 1.04 },
        duration: 800,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    });

    // Button body gradient (neon cyan-to-teal)
    const buildBtnGfx = (bx: number, by: number) => {
      const g = this.add.graphics();
      g.fillGradientStyle(t.buttonA, t.buttonA, t.buttonB, t.buttonB, 0.88, 0.88, 0.72, 0.72);
      g.fillRoundedRect(bx - btnW / 2, by - btnH / 2, btnW, btnH, rad);
      // Top highlight (glossy white-to-cyan shine)
      const hlGfx = this.add.graphics();
      hlGfx.fillGradientStyle(0xffffff, 0xffffff, t.accent, t.accent, 0.28, 0.28, 0, 0);
      hlGfx.fillRoundedRect(bx - btnW / 2 + 2, by - btnH / 2, btnW - 4, btnH * 0.42,
        { tl: rad, tr: rad, bl: 0, br: 0 });
    };
    buildBtnGfx(leftX, leftY);
    buildBtnGfx(rightX, rightY);

    const createButtonSweep = (bx: number, by: number) => {
      const sweepStartX = bx - btnW / 2 - this.scaleX(26);
      const sweepEndX = bx + btnW / 2 + this.scaleX(26);
      const sweep = this.add.rectangle(
        sweepStartX,
        by,
        this.scaleX(24),
        btnH * 0.95,
        0xffffff,
        0.18,
      ).setAngle(-18).setBlendMode(Phaser.BlendModes.ADD);

      const maskGfx = this.make.graphics({ x: 0, y: 0 });
      maskGfx.fillStyle(0xffffff);
      maskGfx.fillRoundedRect(bx - btnW / 2, by - btnH / 2, btnW, btnH, rad);
      sweep.setMask(maskGfx.createGeometryMask());

      this.ambientFxTweens.push(this.tweens.add({
        targets: sweep,
        x: sweepEndX,
        duration: 1300,
        ease: 'Quad.easeOut',
        repeat: -1,
        repeatDelay: 1700,
        onRepeat: () => sweep.setX(sweepStartX),
      }));
    };
    createButtonSweep(leftX, leftY);
    createButtonSweep(rightX, rightY);

    // Dynamic overlay graphics for rounded stroke/fill (positioned at button center)
    this.drawBtnGfxL = this.add.graphics({ x: leftX, y: leftY });
    this.redrawBtnGfx(this.drawBtnGfxL, btnW, btnH, rad, 0, 0, t.accent, 0.95, line);
    this.drawBtnGfxR = this.add.graphics({ x: rightX, y: rightY });
    this.redrawBtnGfx(this.drawBtnGfxR, btnW, btnH, rad, 0, 0, t.accent, 0.95, line);

    // Interactive hit areas (invisible — visuals come from Graphics above)
    this.drawBtnBg = this.add.rectangle(leftX, leftY, btnW, btnH, 0x000000, INPUT_HIT_ALPHA)
      .setDepth(2100)
      .setInteractive({ useHandCursor: true });
    this.drawBtnAltBg = this.add.rectangle(rightX, rightY, btnW, btnH, 0x000000, INPUT_HIT_ALPHA)
      .setDepth(2100)
      .setInteractive({ useHandCursor: true });

    this.drawBtnText = this.makeText(leftX, leftY, 'Draw', {
      fontFamily: 'Inter, Arial, sans-serif',
      fontSize: this.fontPx(22, 14),
      color: t.text,
      stroke: hexColor(t.bgBottom),
      strokeThickness: this.strokePx(2, 1),
      fontStyle: '800',
    }).setOrigin(0.5).setShadow(0, this.scaleX(1), hexColor(t.accent), this.scaleX(8), false, true);
    this.drawBtnAltText = this.makeText(rightX, rightY, 'Draw', {
      fontFamily: 'Inter, Arial, sans-serif',
      fontSize: this.fontPx(22, 14),
      color: t.text,
      stroke: hexColor(t.bgBottom),
      strokeThickness: this.strokePx(2, 1),
      fontStyle: '800',
    }).setOrigin(0.5).setShadow(0, this.scaleX(1), hexColor(t.accent), this.scaleX(8), false, true);

    this.drawBtnBg.on('pointerdown', () => this.doDrawLeft());
    this.drawBtnAltBg.on('pointerdown', () => this.doDrawRight());
  }

  /* ==================================================================
   *  COMBO PANEL – right side
   * ================================================================== */

  private buildComboPanel(w: number, h: number): void {
    const t = this.theme;
    // 12 combos in 2-column, 6-row grid (left col then right col per row)
    const comboGrid: [ComboType, ComboType][] = [
      [ComboType.THREE_CLOVERS, ComboType.THREE_MOONS],
      [ComboType.THREE_HEARTS, ComboType.THREE_STARS],
      [ComboType.THREE_CROWNS, ComboType.THREE_HORSESHOES],
      [ComboType.ONE_PAIR, ComboType.JOKER],
      [ComboType.FULL_HOUSE, ComboType.FIVE_DIFFERENT],
      [ComboType.FOUR_OF_A_KIND, ComboType.FIVE_OF_A_KIND],
    ];

    const buildCell = (cx: number, iy: number, cellW: number, ih: number, combo: ComboType) => {
      const cellRad = this.scaleX(7);

      // ── Cell body: dark panel gradient ────────────────────────────────
      const cellGfx = this.add.graphics();
      cellGfx.fillGradientStyle(t.panelA, t.panelA, t.panelB, t.panelB, 0.96);
      cellGfx.fillRoundedRect(cx - cellW / 2, iy - ih / 2, cellW, ih, cellRad);

      // ── Top gloss highlight ────────────────────────────────────────────
      const cellHlGfx = this.add.graphics();
      cellHlGfx.fillGradientStyle(0xffffff, 0xffffff, t.accent, t.accent, 0.14, 0.14, 0, 0);
      cellHlGfx.fillRoundedRect(cx - cellW / 2 + this.scaleX(1), iy - ih / 2, cellW - this.scaleX(2), ih * 0.36,
        { tl: cellRad, tr: cellRad, bl: 0, br: 0 });
      cellHlGfx.setBlendMode(Phaser.BlendModes.ADD);

      // ── Outer border ──────────────────────────────────────────────────
      const cellStrokeGfx = this.add.graphics();
      cellStrokeGfx.lineStyle(Math.max(1, Math.round(this.cabinetScale * 2)), t.strokeSoft, 0.6);
      cellStrokeGfx.strokeRoundedRect(cx - cellW / 2, iy - ih / 2, cellW, ih, cellRad);

      // ── Pulse glow overlay (shown when combo matches) ─────────────────
      const pulseGlow = this.add.rectangle(cx, iy, cellW + this.scaleX(8), ih + this.scaleX(6), t.accent, 0)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setVisible(false);
      this.comboPulseGlows.push(pulseGlow);
      this.comboGlowTweens.push(null);

      // bg is repainted in refresh() to flash the cell's match/picked state,
      // so it must render *behind* the preview icons and score text. Leave it
      // at default depth (insertion order) — adding setDepth(2100) here would
      // overlay it on top of preview/scoreText and blank out the cell content
      // once refresh() bumps its fill alpha.
      const bg = this.add.rectangle(cx, iy, cellW, ih, 0x000000, INPUT_HIT_ALPHA);
      this.comboBgs.push(bg);
      this.comboRightBgs.push(bg);

      const preview = this.createComboPreviewRow(cx, iy, combo, ih, cellW);
      this.comboPreviewRows.push(preview);

      // Score text (shown only after combo is picked – centered in cell)
      const scoreText = this.makeText(cx, iy, '', {
        fontFamily: 'Inter, Arial, sans-serif',
        fontSize: this.fontPx(this.layout.profile === 'portrait' ? 24 : 16, 12),
        color: hexColor(t.good),
        stroke: hexColor(t.bgTop),
        strokeThickness: this.strokePx(this.layout.profile === 'portrait' ? 2 : 1.4, 1),
        fontStyle: '800',
      }).setOrigin(0.5).setAlpha(0);
      this.comboScoreTexts.push(scoreText);

      bg.setInteractive({ useHandCursor: true });
      bg.on('pointerdown', () => this.pickCombo(combo));
      this.comboTypes.push(combo);
    };

    comboGrid.forEach(([leftCombo, rightCombo], row) => {
      const iy = this.comboRowCenterY(row);
      const ih = this.comboCellH(row);
      const leftX = this.scalePos(this.layout.rightCellLeft.x) + this.comboOffsetX();
      const rightX = this.scalePos(this.layout.rightCellRight.x) + this.comboOffsetX();
      const cellW_L = this.scaleX(this.layout.rightCellLeft.w - 4);
      const cellW_R = this.scaleX(this.layout.rightCellRight.w - 4);

      buildCell(leftX, iy, cellW_L, ih, leftCombo);
      buildCell(rightX, iy, cellW_R, ih, rightCombo);
    });

    // Start rotating icons for generic combos
    this.startGenericComboRotation();
  }

  /* ==================================================================
   *  INFO OVERLAY
   * ================================================================== */

  private buildInfoOverlay(): void {
    const t = this.theme;
    const safeBottomY = this.safeTop + this.safeH;
    const infoY = Math.min(this.cabinetTop + this.mainH + this.scaleX(18), safeBottomY - this.scaleX(14));
    this.infoText = this.makeText(this.safeCx, infoY, '', {
      fontFamily: 'Inter, Arial, sans-serif',
      fontSize: this.fontPx(14, 11),
      color: hexColor(t.accent3),
      stroke: hexColor(t.bgBottom),
      strokeThickness: this.strokePx(1, 1),
      fontStyle: '700',
    }).setOrigin(0.5).setAlpha(0);
  }

  private buildHomeButton(): void {
    const t = this.theme;
    const portrait = this.layout.profile === 'portrait';
    const btnW = Math.max(this.scaleX(portrait ? 130 : 146), this.headerCellW);
    const btnH = this.headerCellH;
    const rad = this.scaleX(8);
    const x = this.headerLeft + btnW / 2;
    const y = this.headerRowTopY;
    const stroke = Math.max(1, Math.round(this.cabinetScale * 2));

    // Rounded visual graphics (positioned at button center)
    this.homeBtnGfx = this.add.graphics({ x, y }).setDepth(2200);
    this.redrawBtnGfx(this.homeBtnGfx, btnW, btnH, rad, t.panelB, 0.92, t.accent, 0.8, stroke);

    // Invisible hit area
    this.homeBtnBg = this.add.rectangle(x, y, btnW, btnH, 0x000000, INPUT_HIT_ALPHA)
      .setDepth(2202)
      .setInteractive({ useHandCursor: true });

    this.homeBtnText = this.makeText(x, y, 'Exit Game', {
      fontFamily: 'Inter, Arial, sans-serif',
      fontSize: this.fontPx(portrait ? 14 : 13, 11),
      color: t.text,
      stroke: hexColor(t.bgTop),
      strokeThickness: this.strokePx(1.4, 1),
      fontStyle: '700',
    }).setOrigin(0.5).setDepth(2201);

    this.homeBtnBg.on('pointerover', () => {
      if (this.homeBtnGfx) this.redrawBtnGfx(this.homeBtnGfx, btnW, btnH, rad, t.panelA, 0.96, t.accent, 1, stroke);
      this.homeBtnText?.setColor('#ffffff');
    });
    this.homeBtnBg.on('pointerout', () => {
      if (this.homeBtnGfx) this.redrawBtnGfx(this.homeBtnGfx, btnW, btnH, rad, t.panelB, 0.92, t.accent, 0.8, stroke);
      this.homeBtnText?.setColor(t.text);
    });
    this.homeBtnBg.on('pointerdown', () => this.goHomeFromGame());
  }

  /* ==================================================================
   *  SOCKET
   * ================================================================== */

  private setupSocketListeners(): void {
    const socket = this.sm.getSocket();
    socket.on('ONLINE_STATS', (data) => this.onOnlineStats(data));
    socket.emit('GET_ONLINE_STATS');

    socket.on('ROUND_START', (data) => {
      // Immediately destroy any lingering round-end popup
      this.destroyRoundEndPopup();
      this.pendingRoundEndResult = null;
      const previousRoom = this.roomState;
      this.roomState = data.room;
      this.syncGameRoute();
      this.maybeHandleRoundTransition(previousRoom);
      this.findMe();
      if (!this.myPlayer) {
        this.requestStateResync('round-start-missing-player');
        return;
      }
      this.notifyRoundStartIfBackground();
      this.resetRoundSpecialState();
      this.leftDrawUsed = false;
      this.rightDrawUsed = false;
      this.showInfo(`Runde ${this.roomState.currentRound} startet`);
      this.playRoundStartImpact();
      this.spinAllReels();
      this.refresh();
    });

    socket.on('STATE_UPDATE', (data) => {
      const previousRoom = this.roomState;
      const prev = this.myPlayer?.reels ? [...this.myPlayer.reels] : [];
      const prevDrawsLeft = this.myPlayer?.drawsLeft ?? 0;
      const prevStatus = this.myPlayer?.status;
      const prevDeadEnd = this.myPlayer ? this.isDeadEndState(this.myPlayer) : false;
      this.roomState = data.room;
      this.syncGameRoute();
      this.maybeHandleRoundTransition(previousRoom);
      this.findMe();
      if (!this.myPlayer) {
        this.requestStateResync('state-update-missing-player');
        return;
      }
      this.refreshBackgroundRoundNotification();
      // Reset draw buttons when draws are replenished (after combo pick)
      if (this.myPlayer.drawsLeft > prevDrawsLeft) {
        this.leftDrawUsed = false;
        this.rightDrawUsed = false;
        this.clearLockVisuals();
      }
      const changed = !prev.length || prev.some((r, i) => r !== this.myPlayer.reels[i]);
      const drawConsumed = this.myPlayer.drawsLeft < prevDrawsLeft;
      // Always animate when a draw was consumed, even if symbols end up identical.
      if ((drawConsumed || changed) && prev.length > 0 && !this.spinning) this.spinReels();
      this.maybeTriggerSpecialSpin(prevStatus, prevDeadEnd);
      this.refresh();
      this.maybeShowDeferredRoundEnd();
    });

    socket.on('ROUND_END', (data) => {
      this.roomState = data.room;
      this.syncGameRoute();
      this.findMe();
      this.refreshBackgroundRoundNotification();
      this.pendingRoundEndResult = data.result;
      this.maybeShowDeferredRoundEnd();
    });

    socket.on('GAME_END', (data) => {
      void roundNotificationManager.cancelNextRoundNotification();
      this.roomState = data.room;
      const code = normalizeRoomCode(data.room?.roomId);
      if (code) setAppRoute({ kind: 'result', roomId: code });
      this.cleanupListeners();
      this.scene.start('ResultScene', { result: data.result, roomState: data.room });
    });

    socket.on('ERROR', (data) => this.showInfo(data.message));

    socket.on('RECONNECTED', (data) => {
      const previousRoom = this.roomState;
      this.roomState = data.room;
      this.syncGameRoute();
      this.maybeHandleRoundTransition(previousRoom);
      this.findMe();
      this.resyncInFlight = false;
      this.queueSpecialSpinForCurrentState();
      this.refreshBackgroundRoundNotification();
      this.refresh();
      this.showInfo('Wieder verbunden');
    });
  }

  private cleanupListeners(): void {
    const s = this.sm.getSocket();
    s.off('ROUND_START'); s.off('STATE_UPDATE');
    s.off('ROUND_END'); s.off('GAME_END');
    s.off('ERROR'); s.off('RECONNECTED');
    s.off('ONLINE_STATS');
  }

  private isDeadEndState(player: PublicPlayerState | undefined): boolean {
    if (!player || player.status !== PlayerStatus.PLAYING) return false;
    if (player.drawsLeft > 0) return false;
    return !player.remainingCombos.some((ct) => validateCombo(player.reels, ct));
  }

  private resetRoundSpecialState(): void {
    this.pendingSpecialSpin = 'none';
    this.activeSpecialSpin = 'none';
    this.reelOverrideSpecialSpin = 'none';
    this.specialFlagSpinDone = false;
    this.specialCrossSpinDone = false;
  }

  private queueSpecialSpinForCurrentState(): void {
    if (!this.myPlayer) return;
    if (this.myPlayer.status === PlayerStatus.FINISHED && !this.specialFlagSpinDone) {
      this.queueSpecialSpin('flags');
      return;
    }
    if (this.isDeadEndState(this.myPlayer) && !this.specialCrossSpinDone) {
      this.queueSpecialSpin('crosses');
    }
  }

  private maybeTriggerSpecialSpin(prevStatus: PlayerStatus | undefined, prevDeadEnd: boolean): void {
    if (!this.myPlayer) return;
    const nowFinished = this.myPlayer.status === PlayerStatus.FINISHED;
    const nowDeadEnd = this.isDeadEndState(this.myPlayer);

    if (nowFinished && prevStatus !== PlayerStatus.FINISHED && !this.specialFlagSpinDone) {
      this.queueSpecialSpin('flags');
      return;
    }
    if (nowDeadEnd && !prevDeadEnd && !this.specialCrossSpinDone) {
      this.queueSpecialSpin('crosses');
      return;
    }

    // Reconnect or delayed state sync fallback.
    this.queueSpecialSpinForCurrentState();
  }

  private queueSpecialSpin(kind: SpecialSpinKind): void {
    if (kind === 'none') return;
    if (kind === 'flags' && (this.specialFlagSpinDone || this.reelOverrideSpecialSpin === 'flags')) return;
    if (kind === 'crosses' && (this.specialCrossSpinDone || this.reelOverrideSpecialSpin === 'crosses')) return;
    if (this.pendingSpecialSpin !== kind) this.pendingSpecialSpin = kind;
    this.runPendingSpecialSpin();
  }

  private runPendingSpecialSpin(): void {
    if (!this.myPlayer) return;
    if (this.spinning || this.activeSpecialSpin !== 'none' || this.pendingSpecialSpin === 'none') return;

    const kind = this.pendingSpecialSpin;
    this.pendingSpecialSpin = 'none';
    this.activeSpecialSpin = kind;
    if (kind === 'flags') this.specialFlagSpinDone = true;
    if (kind === 'crosses') this.specialCrossSpinDone = true;
    this.spinAllReelsToTexture(kind);
  }

  private textureForSpecialSpin(kind: SpecialSpinKind): string {
    return kind === 'flags' ? SPECIAL_FLAG_TEXTURE_KEY : SPECIAL_X_TEXTURE_KEY;
  }

  private requestStateResync(reason: string): void {
    if (!this.sm?.playerId || !this.sm?.roomId) return;
    const now = Date.now();
    if (this.resyncInFlight) return;
    if (now - this.lastResyncAttemptAt < 1500) return;

    this.lastResyncAttemptAt = now;
    this.resyncInFlight = true;

    if (this.sm.isLocalMode()) {
      setAppRoute({ kind: 'gameOffline' }, { replace: true });
    } else {
      const roomId = normalizeRoomCode(this.sm.roomId);
      if (roomId) {
        setAppRoute({ kind: 'game', roomId }, { replace: true });
      }
    }
    this.showInfo('Synchronisiere...');
    console.warn(`[GameScene] State resync requested: ${reason}`);

    this.sm.getSocket().emit('RECONNECT', {
      playerId: this.sm.playerId,
      roomId: this.sm.roomId,
    }, (res) => {
      this.resyncInFlight = false;
      if (res.success) return;

      console.warn('[GameScene] State resync failed:', res.error ?? 'unknown');
      this.showInfo('Session abgelaufen');
      void roundNotificationManager.cancelNextRoundNotification();
      this.sm.clearSession();
      setAppRoute({ kind: 'home' }, { replace: true });
      this.time.delayedCall(420, () => {
        this.cleanupListeners();
        this.scene.start('LobbyScene');
      });
    });
  }

  /* ==================================================================
   *  ACTIONS
   * ================================================================== */

  private goHomeFromGame(): void {
    if (this.navigatingHome) return;
    this.navigatingHome = true;
    void roundNotificationManager.cancelNextRoundNotification();
    this.cleanupListeners();
    this.sm.logoutAndResetConnection();
    setAppRoute({ kind: 'home' }, { replace: true });
    this.time.delayedCall(60, () => {
      this.scene.start('LobbyScene');
    });
  }

  private toggleLock(i: number): void {
    if (!this.myPlayer || this.myPlayer.status !== PlayerStatus.PLAYING || this.spinning) return;
    if (this.isDeadEndState(this.myPlayer)) return;
    if (this.pendingSpecialSpin !== 'none' || this.activeSpecialSpin !== 'none' || this.reelOverrideSpecialSpin !== 'none') return;
    const s = this.sm.getSocket();
    if (this.myPlayer.lockedReels[i]) s.emit('UNLOCK_REEL', { reelIndex: i });
    else s.emit('LOCK_REEL', { reelIndex: i });
  }

  private doDrawLeft(): void {
    if (!this.myPlayer || this.myPlayer.status !== PlayerStatus.PLAYING) return;
    if (this.myPlayer.drawsLeft <= 0 || this.spinning || this.leftDrawUsed) return;
    this.leftDrawUsed = true;
    this.pulseDrawButton(true);
    this.sm.getSocket().emit('DRAW');
  }

  private doDrawRight(): void {
    if (!this.myPlayer || this.myPlayer.status !== PlayerStatus.PLAYING) return;
    if (this.myPlayer.drawsLeft <= 0 || this.spinning || this.rightDrawUsed) return;
    this.rightDrawUsed = true;
    this.pulseDrawButton(false);
    this.sm.getSocket().emit('DRAW');
  }

  private pickCombo(ct: ComboType): void {
    if (!this.myPlayer || this.myPlayer.status !== PlayerStatus.PLAYING || this.spinning) return;
    if (!this.myPlayer.remainingCombos.includes(ct)) return;
    if (!validateCombo(this.myPlayer.reels, ct)) return;
    playSfx(this, SFX_POINTS_KEY, {
      volume: 0.28,
      rate: Phaser.Math.FloatBetween(0.98, 1.04),
    });
    const comboIndex = this.comboTypes.indexOf(ct);
    if (comboIndex >= 0) {
      const glow = this.comboPulseGlows[comboIndex];
      if (glow) {
        this.tweens.add({
          targets: glow,
          alpha: { from: 0.8, to: 0 },
          scaleX: { from: 1.02, to: 1.2 },
          scaleY: { from: 1.02, to: 1.2 },
          duration: 280,
          ease: 'Cubic.easeOut',
          onComplete: () => {
            glow.setScale(1);
            glow.setVisible(false);
          },
        });
      }
      this.emitSparkBurst(this.comboBgs[comboIndex].x, this.comboBgs[comboIndex].y, 0x00ffee, 16);
    }
    this.clearLockVisuals();
    this.sm.getSocket().emit('PICK_COMBO', { comboType: ct });
  }

  /* ==================================================================
   *  REEL ANIMATION
   * ================================================================== */

  private clearLockVisuals(): void {
    for (let i = 0; i < REEL_COUNT; i++) {
      this.lockIcons[i]?.setText('');
      this.reelBgs[i]?.setStrokeStyle(
        Math.max(1, Math.round(this.cabinetScale * 2)),
        CABINET.strokeSoft,
        0.2,
      );
      this.reelGlows[i]?.setAlpha(0);
    }
  }

  private scheduleSpinRelease(delayMs: number, onRelease?: () => void): void {
    this.spinReleaseTimer?.destroy();
    this.spinReleaseTimer = this.time.delayedCall(delayMs, () => {
      this.spinning = false;
      onRelease?.();
      this.runPendingSpecialSpin();
      if (!this.spinning) this.refresh();
      this.maybeShowDeferredRoundEnd();
    });
  }

  private spinAllReels(): void {
    if (!this.myPlayer) return;
    this.spinning = true;
    let maxDur = 0;
    for (let i = 0; i < REEL_COUNT; i++) {
      const dur = 600 + i * 200;
      maxDur = Math.max(maxDur, dur);
      this.animateReel(i, this.myPlayer.reels[i], dur);
    }
    this.scheduleSpinRelease(maxDur + 120);
  }

  private spinReels(): void {
    if (!this.myPlayer) return;
    this.spinning = true;
    let maxDur = 0;
    let animated = 0;
    for (let i = 0; i < REEL_COUNT; i++) {
      if (!this.myPlayer.lockedReels[i]) {
        const dur = 400 + i * 150;
        maxDur = Math.max(maxDur, dur);
        animated++;
        this.animateReel(i, this.myPlayer.reels[i], dur);
      }
    }
    if (animated === 0) {
      this.spinning = false;
      this.runPendingSpecialSpin();
      this.refresh();
      return;
    }
    this.scheduleSpinRelease(maxDur + 120);
  }

  private spinAllReelsToTexture(kind: SpecialSpinKind): void {
    if (!this.myPlayer || kind === 'none') return;
    const textureKey = this.textureForSpecialSpin(kind);
    if (!this.textures.exists(textureKey)) {
      this.activeSpecialSpin = 'none';
      this.reelOverrideSpecialSpin = 'none';
      this.refresh();
      return;
    }

    this.spinning = true;
    let maxDur = 0;
    for (let i = 0; i < REEL_COUNT; i++) {
      const dur = 520 + i * 160;
      maxDur = Math.max(maxDur, dur);
      this.animateReelToTexture(i, textureKey, dur);
    }
    this.scheduleSpinRelease(maxDur + 130, () => {
      this.reelOverrideSpecialSpin = kind;
      this.activeSpecialSpin = 'none';
      if (kind === 'flags') {
        playSfx(this, SFX_FINISH_KEY, { volume: 0.36 });
      }
    });
  }

  private animateReel(idx: number, final: GameSymbol, dur: number): void {
    playSfx(this, SFX_SPIN_WHEEL_KEY, {
      volume: 0.18,
      rate: Phaser.Math.FloatBetween(0.96, 1.04),
    });

    const syms = Object.keys(SYM) as GameSymbol[];
    const bg = this.reelBgs[idx];
    const x = this.reelCenterX(idx);
    const yCenter = this.reelY;
    const innerH = this.reelInnerH();
    const innerW = this.reelInnerW();
    const symbolSize = Math.min(innerW * 0.88, innerH * 0.36);
    const slotH = innerH / 3; // height per symbol slot

    // Determine the final 3 symbols: top-ghost, center (final), bottom-ghost
    const finalTopSym = syms[Math.floor(Math.random() * syms.length)];
    const finalBottomSym = syms[Math.floor(Math.random() * syms.length)];

    const stripSymCount = 12;
    const stripContainer = this.add.container(x, yCenter);

    const maskGfx = this.make.graphics({ x: 0, y: 0 });
    maskGfx.fillStyle(0xffffff);
    maskGfx.fillRect(x - innerW / 2, yCenter - innerH / 2, innerW, innerH);
    stripContainer.setMask(maskGfx.createGeometryMask());

    // Final 3 go FIRST: after scroll they end up in the visible area
    const stripSyms: GameSymbol[] = [finalTopSym, final, finalBottomSym];
    for (let s = 0; s < stripSymCount - 3; s++) {
      stripSyms.push(syms[Math.floor(Math.random() * syms.length)]);
    }

    const totalStripH = stripSymCount * slotH;
    const startY = -innerH / 2 - totalStripH + innerH;

    const children: (Phaser.GameObjects.Image | Phaser.GameObjects.Text)[] = [];
    stripSyms.forEach((sym, si) => {
      const sy = startY + si * slotH + slotH / 2;
      const cfg = SYM[sym];
      // First 3 symbols are the final visible ones (top-ghost, center, bottom-ghost)
      // Dim the ghost positions (index 0 and 2) to match resting alpha
      const isTopGhost = si === 0;
      const isBottomGhost = si === 2;
      const itemAlpha = isTopGhost || isBottomGhost ? 0.22 : 1.0;
      const textureKey = this.textureKeyForSymbol(sym);
      if (textureKey) {
        const displaySize = this.textureDisplaySizeForSymbol(sym, textureKey, symbolSize);
        const img = this.add.image(0, sy, textureKey)
          .setDisplaySize(displaySize, displaySize)
          .setAlpha(itemAlpha);
        stripContainer.add(img);
        children.push(img);
      } else {
        const txt = this.makeText(0, sy, cfg.char, {
          fontFamily: 'Arial Black, Impact, sans-serif',
          fontSize: this.fontPx(44, 20),
          color: '#' + cfg.color.toString(16).padStart(6, '0'),
          stroke: '#000000',
          strokeThickness: Math.max(2, Math.round(this.cabinetScale * 4)),
        }).setOrigin(0.5).setAlpha(itemAlpha);
        stripContainer.add(txt);
        children.push(txt);
      }
    });

    // Hide real reel elements during animation
    this.reelSprites[idx]?.setAlpha(0);
    this.reelGhostTopSprites[idx]?.setAlpha(0);
    this.reelGhostBottomSprites[idx]?.setAlpha(0);
    this.reelTexts[idx]?.setAlpha(0);
    this.reelGhostTopTexts[idx]?.setAlpha(0);
    this.reelGhostBottomTexts[idx]?.setAlpha(0);

    bg.setStrokeStyle(Math.max(1, Math.round(this.cabinetScale * 2)), CABINET.strokeSoft, 0.2);

    const scrollDist = totalStripH - innerH;

    this.tweens.add({
      targets: children,
      y: `+=${scrollDist}`,
      duration: dur,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        stripContainer.destroy();
        maskGfx.destroy();
        // Set the real reel visuals to match the final strip state exactly
        this.setReelVisual(idx, final);
        // Also set ghost sprites/texts through the same selected-icon mapping.
        this.setGhostVisual(this.reelGhostTopSprites[idx], this.reelGhostTopTexts[idx], finalTopSym);
        this.setGhostVisual(this.reelGhostBottomSprites[idx], this.reelGhostBottomTexts[idx], finalBottomSym);
        // Cache so a scene.restart on rotation can re-apply the same ghosts
        // instead of falling back to STAR.
        this.ghostTopSyms[idx] = finalTopSym;
        this.ghostBottomSyms[idx] = finalBottomSym;
      },
    });
  }

  private animateReelToTexture(idx: number, textureKey: string, dur: number): void {
    playSfx(this, SFX_SPIN_WHEEL_KEY, {
      volume: 0.18,
      rate: Phaser.Math.FloatBetween(0.96, 1.04),
    });

    const bg = this.reelBgs[idx];
    const x = this.reelCenterX(idx);
    const yCenter = this.reelY;
    const innerH = this.reelInnerH();
    const innerW = this.reelInnerW();
    const symbolSize = Math.min(innerW * 0.88, innerH * 0.36);
    const slotH = innerH / 3;

    const stripSymCount = 12;
    const stripContainer = this.add.container(x, yCenter);

    const maskGfx = this.make.graphics({ x: 0, y: 0 });
    maskGfx.fillStyle(0xffffff);
    maskGfx.fillRect(x - innerW / 2, yCenter - innerH / 2, innerW, innerH);
    stripContainer.setMask(maskGfx.createGeometryMask());

    const totalStripH = stripSymCount * slotH;
    const startY = -innerH / 2 - totalStripH + innerH;
    const children: Phaser.GameObjects.Image[] = [];
    for (let si = 0; si < stripSymCount; si++) {
      const sy = startY + si * slotH + slotH / 2;
      const isGhost = si === 0 || si === 2;
      const img = this.add.image(0, sy, textureKey)
        .setDisplaySize(symbolSize, symbolSize)
        .setAlpha(isGhost ? 0.22 : 0.98);
      stripContainer.add(img);
      children.push(img);
    }

    this.reelSprites[idx]?.setAlpha(0);
    this.reelGhostTopSprites[idx]?.setAlpha(0);
    this.reelGhostBottomSprites[idx]?.setAlpha(0);
    this.reelTexts[idx]?.setAlpha(0);
    this.reelGhostTopTexts[idx]?.setAlpha(0);
    this.reelGhostBottomTexts[idx]?.setAlpha(0);
    bg.setStrokeStyle(Math.max(1, Math.round(this.cabinetScale * 2)), CABINET.strokeSoft, 0.2);

    const scrollDist = totalStripH - innerH;
    this.tweens.add({
      targets: children,
      y: `+=${scrollDist}`,
      duration: dur,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        stripContainer.destroy();
        maskGfx.destroy();
        this.setReelTextureVisual(idx, textureKey);
      },
    });
  }

  private setReelTextureVisual(idx: number, textureKey: string): void {
    const displaySize = this.reelSymbolBaseSize();
    this.reelTexts[idx]?.setAlpha(0);
    this.reelGhostTopTexts[idx]?.setAlpha(0);
    this.reelGhostBottomTexts[idx]?.setAlpha(0);
    this.reelSprites[idx]?.setTexture(textureKey).setDisplaySize(displaySize, displaySize);
    this.applyCenterSpriteStyle(this.reelSprites[idx]);
    this.setGhostSpriteTexture(this.reelGhostTopSprites[idx], textureKey);
    this.setGhostSpriteTexture(this.reelGhostBottomSprites[idx], textureKey);
    this.reelGhostTopSprites[idx]?.setDisplaySize(displaySize, displaySize);
    this.reelGhostBottomSprites[idx]?.setDisplaySize(displaySize, displaySize);
    this.applyGhostSpriteStyle(this.reelGhostTopSprites[idx]);
    this.applyGhostSpriteStyle(this.reelGhostBottomSprites[idx]);
  }

  /* ==================================================================
   *  REFRESH UI
   * ================================================================== */

  private findMe(): void {
    if (!this.roomState) return;
    const m = this.roomState.players.find(p => p.id === this.sm.playerId);
    this.myPlayer = m ?? undefined!;
  }

  private refresh(): void {
    const t = this.theme;
    if (!this.roomState || !this.myPlayer) return;
    this.syncGameRoute();
    const roundScore = this.myPlayer.roundScore ?? 0;
    const totalScore = this.myPlayer.totalScore ?? 0;
    // During round/game end, roundScore has already been transferred into totalScore on the server.
    // Prevent visual double-counting in the top-right display.
    const displayRoundScore = this.roomState.status === RoomStatus.PLAYING ? roundScore : 0;
    const liveScore = totalScore + displayRoundScore;

    // Top bar
    this.roundText.setText(`Round ${this.roomState.currentRound}/${this.roomState.totalRounds}`);
    this.scoreText.setText(this.formatComma(liveScore));
    this.roundScoreText?.setText(this.formatComma(displayRoundScore));

    // Draw buttons: each treated independently
    const canDraw = this.myPlayer.status === PlayerStatus.PLAYING && this.myPlayer.drawsLeft > 0;
    const leftActive = canDraw && !this.leftDrawUsed;
    const rightActive = canDraw && !this.rightDrawUsed;
    const { w: bw, h: bh, rad: brad } = this.drawBtnGeom;
    const btnLine = Math.max(1, Math.round(this.cabinetScale * 2));

    // Left button
    if (leftActive) {
      this.redrawBtnGfx(this.drawBtnGfxL, bw, bh, brad, 0, 0, t.accent, 1, btnLine);
      this.drawBtnText.setColor(t.text).setAlpha(1);
      this.drawBtnHaloL?.setVisible(true);
    } else {
      this.redrawBtnGfx(this.drawBtnGfxL, bw, bh, brad, t.bgTop, 0.82, t.strokeSoft, 0.42, btnLine);
      this.drawBtnText.setColor(t.dim).setAlpha(0.5);
      this.drawBtnHaloL?.setVisible(false);
    }

    // Right button
    if (rightActive) {
      this.redrawBtnGfx(this.drawBtnGfxR, bw, bh, brad, 0, 0, t.accent, 1, btnLine);
      this.drawBtnAltText.setColor(t.text).setAlpha(1);
      this.drawBtnHaloR?.setVisible(true);
    } else {
      this.redrawBtnGfx(this.drawBtnGfxR, bw, bh, brad, t.bgTop, 0.82, t.strokeSoft, 0.42, btnLine);
      this.drawBtnAltText.setColor(t.dim).setAlpha(0.5);
      this.drawBtnHaloR?.setVisible(false);
    }

    if (this.myPlayer.status === PlayerStatus.FINISHED) {
      this.drawBtnText.setText('Done');
      this.drawBtnAltText.setText('Done');
      this.redrawBtnGfx(this.drawBtnGfxL, bw, bh, brad, t.good, 0.18, t.good, 0.75, btnLine);
      this.redrawBtnGfx(this.drawBtnGfxR, bw, bh, brad, t.good, 0.18, t.good, 0.75, btnLine);
      this.drawBtnHaloL?.setVisible(false);
      this.drawBtnHaloR?.setVisible(false);
    } else {
      this.drawBtnText.setText('Draw');
      this.drawBtnAltText.setText('Draw');
    }

    const deadEndState = this.isDeadEndState(this.myPlayer);
    const keepFlagOverride = this.reelOverrideSpecialSpin === 'flags' && this.myPlayer.status === PlayerStatus.FINISHED;
    const keepCrossOverride = this.reelOverrideSpecialSpin === 'crosses' && deadEndState;
    if (!keepFlagOverride && !keepCrossOverride && this.reelOverrideSpecialSpin !== 'none') {
      this.reelOverrideSpecialSpin = 'none';
    }
    const hasSpecialReelOverride = this.reelOverrideSpecialSpin !== 'none';
    const canToggleReels = this.myPlayer.status === PlayerStatus.PLAYING
      && !this.spinning
      && !deadEndState
      && this.pendingSpecialSpin === 'none'
      && this.activeSpecialSpin === 'none'
      && !hasSpecialReelOverride;

    for (let i = 0; i < REEL_COUNT; i++) {
      const hitArea = this.reelHitAreas[i];
      if (!hitArea) continue;
      if (canToggleReels) hitArea.setInteractive({ useHandCursor: true });
      else hitArea.disableInteractive();
    }
    if (this.spinning) {
      this.comboBgs.forEach((bg) => bg.disableInteractive());
    }

    // Reels
    if (!this.spinning) {
      const specialTextureKey = hasSpecialReelOverride ? this.textureForSpecialSpin(this.reelOverrideSpecialSpin) : null;
      const specialStroke = this.reelOverrideSpecialSpin === 'flags' ? t.good : t.bad;
      const allSyms = Object.keys(SYM) as GameSymbol[];
      const pickRandomOther = (exclude?: GameSymbol): GameSymbol => {
        if (allSyms.length <= 1) return allSyms[0] ?? GameSymbol.STAR;
        let pick: GameSymbol;
        do { pick = allSyms[Math.floor(Math.random() * allSyms.length)]; }
        while (pick === exclude);
        return pick;
      };
      for (let i = 0; i < REEL_COUNT; i++) {
        if (specialTextureKey && this.textures.exists(specialTextureKey)) {
          this.setReelTextureVisual(i, specialTextureKey);
          this.lockIcons[i].setText('');
          this.reelBgs[i].setStrokeStyle(
            Math.max(1, Math.round(this.cabinetScale * 2)),
            specialStroke,
            this.reelOverrideSpecialSpin === 'flags' ? 0.65 : 0.72,
          );
          this.reelGlows[i].setAlpha(this.reelOverrideSpecialSpin === 'flags' ? 0.2 : 0.24);
          this.reelAuras[i]?.setFillStyle(this.reelOverrideSpecialSpin === 'flags' ? t.good : t.bad, 0.12);
          this.lockStateCache[i] = false;
          continue;
        }

        const sym = this.myPlayer.reels[i];
        if (sym) {
          this.setReelVisual(i, sym);
        }

        // Restore ghost top/bottom symbols cached from the most recent spin so
        // they survive scene.restart on orientation change. If nothing has been
        // cached yet (first build), seed with a random non-center symbol so
        // players don't see a column of identical default stars.
        if (!this.ghostTopSyms[i]) this.ghostTopSyms[i] = pickRandomOther(sym);
        if (!this.ghostBottomSyms[i]) this.ghostBottomSyms[i] = pickRandomOther(this.ghostTopSyms[i]);
        this.setGhostVisual(this.reelGhostTopSprites[i], this.reelGhostTopTexts[i], this.ghostTopSyms[i]);
        this.setGhostVisual(this.reelGhostBottomSprites[i], this.reelGhostBottomTexts[i], this.ghostBottomSyms[i]);

        const locked = this.myPlayer.lockedReels[i];
        this.lockIcons[i].setText(locked ? 'LOCK' : '');
        this.lockIcons[i].setColor(locked ? hexColor(t.accent3) : '#000000');
        this.reelBgs[i].setStrokeStyle(
          Math.max(1, Math.round(this.cabinetScale * 2.5)),
          locked ? t.accent3 : t.strokeSoft,
          locked ? 0.9 : 0.28,
        );
        this.reelGlows[i].setAlpha(locked ? 0.22 : 0);
        this.reelAuras[i]?.setFillStyle(locked ? t.accent3 : t.accent, locked ? 0.12 : 0.06);

        if (this.lockCachePrimed && this.lockStateCache[i] !== locked) {
          this.emitReelLockBurst(i, locked);
        }
        this.lockStateCache[i] = locked;
      }
      this.lockCachePrimed = true;

      if (!hasSpecialReelOverride && this.activeSpecialSpin === 'none') {
        this.queueSpecialSpinForCurrentState();
      }
    }

    // Combos
    for (let i = 0; i < this.comboTypes.length; i++) {
      const ct = this.comboTypes[i];
      const remaining = this.myPlayer.remainingCombos.includes(ct);
      const match = remaining && this.myPlayer.reels.length > 0 && validateCombo(this.myPlayer.reels, ct);
      const bg = this.comboBgs[i];
      const scoredPts = this.myPlayer.comboScores?.[ct];

      if (!remaining) {
        // Already picked: dark muted bg, hide icons, show score
        bg.setFillStyle(t.bgTop, 0.96);
        bg.setStrokeStyle(Math.max(1, Math.round(this.cabinetScale * 2)), t.strokeSoft, 0.36);
        this.comboPreviewRows[i]?.setAlpha(0);
        this.comboScoreTexts[i].setText(scoredPts != null ? this.formatComma(scoredPts) : '✓').setAlpha(1);
        this.stopComboGlow(i);
        bg.disableInteractive();
      } else if (match) {
        // Clickable / matching: vivid neon cyan glow
        bg.setFillStyle(t.panelA, 0.98);
        bg.setStrokeStyle(Math.max(1, Math.round(this.cabinetScale * 2.5)), t.accent, 1);
        this.comboPreviewRows[i]?.setAlpha(1);
        this.comboScoreTexts[i].setAlpha(0);
        this.ensureComboGlow(i);
        bg.setInteractive({ useHandCursor: true });
      } else {
        // Default: dark purple bg
        bg.setFillStyle(t.panelB, 0.96);
        bg.setStrokeStyle(Math.max(1, Math.round(this.cabinetScale * 2)), t.strokeSoft, 0.38);
        this.comboPreviewRows[i]?.setAlpha(0.45);
        this.comboScoreTexts[i].setAlpha(0);
        this.stopComboGlow(i);
        bg.disableInteractive();
      }
    }
  }

  /* ==================================================================
   *  TIMER
   * ================================================================== */

  private startTimer(): void {
    this.timerEvent = this.time.addEvent({
      delay: 200, loop: true,
      callback: () => {
        if (!this.roomState) return;

        let pct = 1;
        if (this.roomState.status !== RoomStatus.PLAYING) {
          const tl = this.roomState.roundTimeLeft ?? 0;
          pct = Math.max(0, tl / ROUND_DURATION_MS);
        } else {
          const elapsed = Date.now() - this.roomState.roundStartTime;
          const timeLeft = Math.max(0, ROUND_DURATION_MS - elapsed);
          pct = timeLeft / ROUND_DURATION_MS;
        }

        // Sync the timerBar width (used by legacy references)
        const fillW = this.timerBarFullW * pct;
        this.timerBar.setSize(fillW, this.timerBar.height);

        // Blend from blue (full time) to red (low time)
        const from = 0x2f8cff;
        const to = 0xff3b30;
        const t = 1 - pct;
        const fr = (from >> 16) & 0xff;
        const fg = (from >> 8) & 0xff;
        const fb = from & 0xff;
        const tr = (to >> 16) & 0xff;
        const tg = (to >> 8) & 0xff;
        const tb = to & 0xff;
        const r = Math.round(fr + (tr - fr) * t);
        const g = Math.round(fg + (tg - fg) * t);
        const b = Math.round(fb + (tb - fb) * t);
        this.timerBar.setFillStyle((r << 16) | (g << 8) | b, 1);
      },
    });
  }

  private symbolGlyph(sym: GameSymbol): string {
    const override = getSymbolOverride(sym);
    return override && !override.startsWith('/pickitup/') ? override : SYM[sym]?.char || '?';
  }

  private textureKeyForSymbol(sym: GameSymbol): string | null {
    const overrideTexture = getSymbolOverrideTexture(sym);
    if (overrideTexture && this.textures.exists(overrideTexture.key)) return overrideTexture.key;
    const cfg = SYM[sym];
    if (cfg && this.textures.exists(cfg.textureKey)) return cfg.textureKey;
    return null;
  }

  private reelSymbolBaseSize(): number {
    const innerH = this.reelInnerH();
    const innerW = this.reelInnerW();
    return Math.min(innerW * 0.88, innerH * 0.36);
  }

  private textureDisplaySizeForSymbol(_sym: GameSymbol, _textureKey: string, baseSize: number): number {
    // Keine Code-seitige Padding-/Alpha-Korrektur: Icon-Dateien bestimmen ihre optische Größe selbst.
    return baseSize;
  }

  private setGhostVisual(sprite: Phaser.GameObjects.Image | undefined, text: Phaser.GameObjects.Text | undefined, sym: GameSymbol): void {
    const cfg = SYM[sym];
    if (!cfg) return;
    const textureKey = this.textureKeyForSymbol(sym);
    if (textureKey) {
      const displaySize = this.textureDisplaySizeForSymbol(sym, textureKey, this.reelSymbolBaseSize());
      text?.setAlpha(0);
      this.setGhostSpriteTexture(sprite, textureKey);
      sprite?.setDisplaySize(displaySize, displaySize);
      this.applyGhostSpriteStyle(sprite);
      return;
    }
    sprite?.setVisible(false).setAlpha(0);
    text?.setText(cfg.char)
      .setColor('#' + cfg.color.toString(16).padStart(6, '0'))
      .setAlpha(0.26);
  }

  private setReelVisual(idx: number, sym: GameSymbol): void {
    const config = SYM[sym];
    if (!config) return;

    const override = getSymbolOverride(sym);
    const textOverride = override && !override.startsWith('/pickitup/') ? override : null;
    const textureKey = this.textureKeyForSymbol(sym) || config.textureKey;
    const displaySize = this.textureDisplaySizeForSymbol(sym, textureKey, this.reelSymbolBaseSize());
    const color = '#' + config.color.toString(16).padStart(6, '0');
    const glow = '#' + config.glow.toString(16).padStart(6, '0');
    const hasTexture = this.textures.exists(textureKey) && !textOverride;

    const apply = (txt: Phaser.GameObjects.Text, alpha: number, blur: number) => {
      txt
        .setText(textOverride || config.char)
        .setColor(color)
        .setAlpha(alpha)
        .setShadow(0, 0, glow, blur, true, true);
    };

    if (hasTexture) {
      this.reelTexts[idx]?.setAlpha(0);
      this.reelGhostTopTexts[idx]?.setAlpha(0);
      this.reelGhostBottomTexts[idx]?.setAlpha(0);

      const center = this.reelSprites[idx];
      center?.setVisible(true).setTexture(textureKey).setDisplaySize(displaySize, displaySize);
      this.applyCenterSpriteStyle(center);
      return;
    }

    this.reelSprites[idx]?.setVisible(false).setAlpha(0);
    this.reelGhostTopSprites[idx]?.setVisible(false).setAlpha(0);
    this.reelGhostBottomSprites[idx]?.setVisible(false).setAlpha(0);
    apply(this.reelTexts[idx], 0.98, Math.round(18 * this.cabinetScale));
    // Ghost texts are NOT updated here — they keep their animation-set values
  }

  private comboPatternText(combo: ComboType): string {
    switch (combo) {
      case ComboType.THREE_CLOVERS: return `${this.symbolGlyph(GameSymbol.CLOVER)} ${this.symbolGlyph(GameSymbol.CLOVER)} ${this.symbolGlyph(GameSymbol.CLOVER)}`;
      case ComboType.THREE_MOONS: return `${this.symbolGlyph(GameSymbol.MOON)} ${this.symbolGlyph(GameSymbol.MOON)} ${this.symbolGlyph(GameSymbol.MOON)}`;
      case ComboType.THREE_HEARTS: return `${this.symbolGlyph(GameSymbol.HEART)} ${this.symbolGlyph(GameSymbol.HEART)} ${this.symbolGlyph(GameSymbol.HEART)}`;
      case ComboType.THREE_STARS: return `${this.symbolGlyph(GameSymbol.STAR)} ${this.symbolGlyph(GameSymbol.STAR)} ${this.symbolGlyph(GameSymbol.STAR)}`;
      case ComboType.THREE_CROWNS: return `${this.symbolGlyph(GameSymbol.CROWN)} ${this.symbolGlyph(GameSymbol.CROWN)} ${this.symbolGlyph(GameSymbol.CROWN)}`;
      case ComboType.THREE_HORSESHOES: return `${this.symbolGlyph(GameSymbol.HORSESHOE)} ${this.symbolGlyph(GameSymbol.HORSESHOE)} ${this.symbolGlyph(GameSymbol.HORSESHOE)}`;
      case ComboType.ONE_PAIR: return '? ?';
      case ComboType.JOKER: return '🃏';
      case ComboType.FULL_HOUSE: return '? ? ?  ? ?';
      case ComboType.FIVE_DIFFERENT: return `${this.symbolGlyph(GameSymbol.STAR)} ${this.symbolGlyph(GameSymbol.MOON)} ${this.symbolGlyph(GameSymbol.HORSESHOE)} ${this.symbolGlyph(GameSymbol.HEART)} ${this.symbolGlyph(GameSymbol.CROWN)}`;
      case ComboType.FOUR_OF_A_KIND: return '? ? ? ?';
      case ComboType.FIVE_OF_A_KIND: return '? ? ? ? ?';
      default: return COMBO_LABELS[combo] ?? combo;
    }
  }

  private comboPatternSymbols(combo: ComboType): GameSymbol[] {
    switch (combo) {
      case ComboType.THREE_CLOVERS:
        return [GameSymbol.CLOVER, GameSymbol.CLOVER, GameSymbol.CLOVER];
      case ComboType.THREE_MOONS:
        return [GameSymbol.MOON, GameSymbol.MOON, GameSymbol.MOON];
      case ComboType.THREE_HEARTS:
        return [GameSymbol.HEART, GameSymbol.HEART, GameSymbol.HEART];
      case ComboType.THREE_STARS:
        return [GameSymbol.STAR, GameSymbol.STAR, GameSymbol.STAR];
      case ComboType.THREE_CROWNS:
        return [GameSymbol.CROWN, GameSymbol.CROWN, GameSymbol.CROWN];
      case ComboType.THREE_HORSESHOES:
        return [GameSymbol.HORSESHOE, GameSymbol.HORSESHOE, GameSymbol.HORSESHOE];
      case ComboType.ONE_PAIR:
        return [GameSymbol.HORSESHOE, GameSymbol.HORSESHOE];
      case ComboType.JOKER:
        return [GameSymbol.STAR, GameSymbol.MOON, GameSymbol.HORSESHOE, GameSymbol.HEART, GameSymbol.CROWN];
      case ComboType.FULL_HOUSE:
        return [GameSymbol.HEART, GameSymbol.HEART, GameSymbol.HEART, GameSymbol.HORSESHOE, GameSymbol.HORSESHOE];
      case ComboType.FIVE_DIFFERENT:
        return [GameSymbol.STAR, GameSymbol.MOON, GameSymbol.HORSESHOE, GameSymbol.HEART, GameSymbol.CROWN];
      case ComboType.FOUR_OF_A_KIND:
        return [GameSymbol.CLOVER, GameSymbol.CLOVER, GameSymbol.CLOVER, GameSymbol.CLOVER];
      case ComboType.FIVE_OF_A_KIND:
        return [GameSymbol.CROWN, GameSymbol.CROWN, GameSymbol.CROWN, GameSymbol.CROWN, GameSymbol.CROWN];
      default:
        return [];
    }
  }

  private createComboPreviewRow(
    x: number,
    y: number,
    combo: ComboType,
    rowHeight: number,
    cellWidth: number,
  ): Phaser.GameObjects.Container {
    const syms = this.comboPatternSymbols(combo);
    const row = this.add.container(x, y);

    // Joker - show special icon
    if (combo === ComboType.JOKER) {
      if (this.textures.exists(SPECIAL_JOKER_TEXTURE_KEY)) {
        const jokerIconSize = Math.min(cellWidth * 0.62, rowHeight * 0.92, this.scaleX(62));
        const jokerIcon = this.add.image(0, 0, SPECIAL_JOKER_TEXTURE_KEY)
          .setDisplaySize(jokerIconSize, jokerIconSize)
          .setAlpha(0.95);
        row.add(jokerIcon);
      } else {
        const jokerText = this.makeText(0, 0, '🃏', {
          fontFamily: 'Arial Black, Impact, sans-serif',
          fontSize: this.fontPx(this.layout.profile === 'portrait' ? 46 : 30, 18),
          color: '#ffd700',
        }).setOrigin(0.5);
        row.add(jokerText);
      }
      return row;
    }

    if (syms.length === 0) return row;

    const usableW = Math.max(this.scaleX(24), cellWidth - this.scaleX(18));
    const iconSizeByWidth = syms.length > 1
      ? usableW / (1 + (syms.length - 1) * 0.78)
      : usableW * 0.68;
    const iconSize = Math.min(this.scaleX(62), rowHeight * 0.9, iconSizeByWidth);
    const gap = syms.length > 1 ? Math.min(iconSize * 0.78, usableW / (syms.length - 1)) : 0;
    const totalW = (syms.length - 1) * gap;
    const startX = -totalW / 2;

    syms.forEach((sym, i) => {
      const cfg = SYM[sym];
      const textureKey = this.textureKeyForSymbol(sym);
      if (textureKey) {
        const displaySize = this.textureDisplaySizeForSymbol(sym, textureKey, iconSize);
        const icon = this.add.image(startX + i * gap, 0, textureKey)
          .setDisplaySize(displaySize, displaySize)
          .setAlpha(0.9)
          .setData('comboIconBaseSize', iconSize);
        row.add(icon);
      } else {
        const fallback = this.makeText(startX + i * gap, 0, cfg.char, {
          fontFamily: 'Arial Black, Impact, sans-serif',
          fontSize: `${Math.max(10, Math.round(iconSize * 0.72))}px`,
          color: '#' + cfg.color.toString(16).padStart(6, '0'),
          stroke: '#210731',
          strokeThickness: Math.max(1, Math.round(this.cabinetScale * 2)),
        }).setOrigin(0.5);
        row.add(fallback);
      }
    });

    return row;
  }

  private startGenericComboRotation(): void {
    const allSymKeys = Object.keys(SYM) as GameSymbol[];
    const symCount = allSymKeys.length;
    let tick = 0;

    // Build a mapping from combo icon index → symbol index offset
    // so that pattern structure is maintained (e.g. 3+2 for FULL_HOUSE)
    const randSym = () => allSymKeys[Math.floor(Math.random() * symCount)];
    const randSymExcluding = (exclude: GameSymbol) => {
      let s: GameSymbol;
      do { s = randSym(); } while (s === exclude);
      return s;
    };

    this.time.addEvent({
      delay: 800, loop: true,
      callback: () => {
        tick++;
        for (let i = 0; i < this.comboTypes.length; i++) {
          const combo = this.comboTypes[i];
          if (!GENERIC_COMBOS.includes(combo)) continue;
          const row = this.comboPreviewRows[i];
          if (!row || row.alpha === 0) continue;

          const children = row.getAll() as (Phaser.GameObjects.Image | Phaser.GameObjects.Text)[];

          // Pick random symbols respecting pattern structure
          let symsForRow: GameSymbol[];
          if (combo === ComboType.ONE_PAIR) {
            const s = randSym();
            symsForRow = [s, s];
          } else if (combo === ComboType.FOUR_OF_A_KIND) {
            const s = randSym();
            symsForRow = [s, s, s, s];
          } else if (combo === ComboType.FIVE_OF_A_KIND) {
            const s = randSym();
            symsForRow = [s, s, s, s, s];
          } else if (combo === ComboType.FULL_HOUSE) {
            const s1 = randSym();
            const s2 = randSymExcluding(s1);
            symsForRow = [s1, s1, s1, s2, s2];
          } else {
            symsForRow = children.map(() => randSym());
          }

          children.forEach((child, ci) => {
            const sym = symsForRow[ci] ?? randSym();
            const cfg = SYM[sym];
            const textureKey = this.textureKeyForSymbol(sym);
            if (child instanceof Phaser.GameObjects.Image && textureKey) {
              const baseSize = Number(child.getData('comboIconBaseSize')) || Math.max(child.displayWidth, child.displayHeight);
              const displaySize = this.textureDisplaySizeForSymbol(sym, textureKey, baseSize);
              child.setTexture(textureKey).setDisplaySize(displaySize, displaySize);
            } else if (child instanceof Phaser.GameObjects.Text) {
              child.setText(cfg.char);
              child.setColor('#' + cfg.color.toString(16).padStart(6, '0'));
            }
          });
        }
      },
    });
  }

  private formatComma(value: number): string {
    return Math.round(value || 0).toLocaleString('de-DE');
  }

  private formatDot(value: number): string {
    return Math.round(value || 0).toLocaleString('de-DE');
  }

  private syncGameRoute(): void {
    if (this.sm?.isLocalMode()) {
      if (this.routeRoomId === '__offline__') return;
      this.routeRoomId = '__offline__';
      setAppRoute({ kind: 'gameOffline' }, { replace: true });
      return;
    }

    const code = normalizeRoomCode(this.roomState?.roomId);
    if (!code || this.routeRoomId === code) return;
    this.routeRoomId = code;
    setAppRoute({ kind: 'game', roomId: code }, { replace: true });
  }

  /* ==================================================================
   *  INFO / ROUND END
   * ================================================================== */

  private showInfo(msg: string): void {
    this.infoText.setText(msg).setAlpha(1);
    this.tweens.add({ targets: this.infoText, alpha: 0, duration: 2500, delay: 1500, ease: 'Power2' });
  }

  private destroyRoundEndPopup(): void {
    this.roundEndPopup.forEach(obj => obj.destroy());
    this.roundEndPopup = [];
  }

  private maybeShowDeferredRoundEnd(): void {
    if (!this.pendingRoundEndResult) return;
    if (this.spinning || this.activeSpecialSpin !== 'none') return;

    // Falls ein Spezial-Spin (z.B. X-Animation) noch nur vorgemerkt ist, zuerst diesen ausführen.
    if (this.pendingSpecialSpin !== 'none') {
      this.runPendingSpecialSpin();
      if (this.spinning || this.activeSpecialSpin !== 'none') return;
    }

    const result = this.pendingRoundEndResult;
    this.pendingRoundEndResult = null;
    this.showRoundEnd(result);
  }

  private showRoundEnd(result: RoundResult): void {
    this.destroyRoundEndPopup();
    const { cx: screenCx, cy: screenCy, w, h } = center(this);
    const cx = this.safeCx || screenCx;
    const cy = this.safeCy || screenCy;
    // All popup sizes/offsets/fonts are authored in design units and scaled to
    // canvas pixels via scaleX so the layout matches the rest of the scene's
    // DPR-aware coordinate space.
    const sx = (v: number) => this.scaleX(v);
    const contentW = Math.max(sx(280), Math.min(this.safeW || w, w));
    const contentH = Math.max(sx(240), Math.min(this.safeH || h, h));
    const overlayW = Math.min(sx(this.layout.profile === 'portrait' ? 440 : 500), contentW - sx(24));
    const overlayH = Math.min(sx(this.layout.profile === 'portrait' ? 360 : 340), contentH - sx(24));
    const topY = cy - overlayH / 2;
    const cornerRad = sx(12);

    // Dark semi-transparent backdrop
    const backdrop = this.add.rectangle(screenCx, screenCy, w, h, 0x000000, 0.58).setDepth(3000);

    // Solid base is required for the Canvas renderer used on iOS; gradient is decorative.
    const overlaySolidGfx = this.add.graphics().setDepth(3001);
    overlaySolidGfx.fillStyle(0x120830, 0.98);
    overlaySolidGfx.fillRoundedRect(cx - overlayW / 2, cy - overlayH / 2, overlayW, overlayH, cornerRad);

    // Glassmorphism popup with neon border
    const overlayGfx = this.add.graphics().setDepth(3002);
    overlayGfx.fillGradientStyle(0x120830, 0x1a0c44, 0x0a0420, 0x120830, 0.96);
    overlayGfx.fillRoundedRect(cx - overlayW / 2, cy - overlayH / 2, overlayW, overlayH, cornerRad);
    // Top shine
    const shineGfx = this.add.graphics().setDepth(3003);
    shineGfx.fillGradientStyle(0x00ffee, 0xff44ff, 0x000000, 0x000000, 0.08, 0.06, 0, 0);
    shineGfx.fillRoundedRect(cx - overlayW / 2 + sx(2), cy - overlayH / 2, overlayW - sx(4), overlayH * 0.25,
      { tl: cornerRad, tr: cornerRad, bl: 0, br: 0 });
    shineGfx.setBlendMode(Phaser.BlendModes.ADD);

    const overlay = this.add.rectangle(cx, cy, overlayW, overlayH, 0x000000, 0)
      .setStrokeStyle(Math.max(2, Math.round(this.cabinetScale * 2)), 0x00ffee, 0.8)
      .setDepth(3004);

    const overlayWBase = overlayW / this.cabinetScale;
    const titleFont = sx(Math.round(Math.max(18, Math.min(22, overlayWBase * 0.058))));
    const rowFont = sx(Math.round(Math.max(13, Math.min(17, overlayWBase * 0.044))));
    const totalFont = sx(Math.round(Math.max(17, Math.min(20, overlayWBase * 0.052))));

    const title = this.makeText(cx, topY + sx(28),
      `RUNDE ${result.round} BEENDET`, {
      fontFamily: 'Inter', fontSize: `${titleFont}px`, color: '#00ffee', fontStyle: '800',
      fixedWidth: overlayW - sx(36), align: 'center',
    }).setOrigin(0.5).setDepth(3005)
      .setShadow(0, 0, '#00ffee', sx(12), true, true);

    const sorted = [...result.playerResults].sort((a, b) => b.roundScore - a.roundScore);
    const rowStartY = topY + sx(76);
    const rowEndY = topY + overlayH - sx(76);
    const rowGap = sorted.length > 1
      ? Math.max(sx(26), Math.min(sx(40), (rowEndY - rowStartY) / (sorted.length - 1)))
      : 0;
    const rows: Phaser.GameObjects.Text[] = [];

    sorted.forEach((pr, i) => {
      const isMe = pr.id === this.sm.playerId;
      const medal = ['🥇', '🥈', '🥉', ''][i] || '';
      const color = isMe ? '#ffd644' : (i === 0 ? '#00ffee' : CABINET.text);
      const t = this.makeText(cx, rowStartY + i * rowGap,
        `${medal} ${pr.name}   ${this.formatComma(pr.roundScore)}`, {
        fontFamily: 'Inter', fontSize: `${rowFont}px`,
        color,
        fontStyle: isMe ? '700' : '500',
        fixedWidth: overlayW - sx(54),
        align: 'center',
      }).setOrigin(0.5).setDepth(3005);
      if (isMe) t.setShadow(0, 0, '#ffd644', sx(8), true, true);
      rows.push(t);
    });

    const myTotal = sorted.find(p => p.id === this.sm.playerId)?.totalScore ?? 0;
    const total = this.makeText(cx, topY + overlayH - sx(30),
      `Gesamt: ${this.formatComma(myTotal)}`, {
      fontFamily: 'Inter', fontSize: `${totalFont}px`, color: '#33ffaa', fontStyle: '700',
      fixedWidth: overlayW - sx(36), align: 'center',
    }).setOrigin(0.5).setDepth(3005)
      .setShadow(0, 0, '#33ffaa', sx(8), true, true);

    this.roundEndPopup = [backdrop, overlaySolidGfx, overlayGfx, shineGfx, overlay, title, total, ...rows];

    // Entrance animation
    const popupElements = [overlaySolidGfx, overlayGfx, shineGfx, overlay, title, total, ...rows] as Phaser.GameObjects.GameObject[];
    popupElements.forEach(obj => {
      if ('setScale' in obj) (obj as any).setScale(0.92);
      if ('setAlpha' in obj) (obj as any).setAlpha(0);
    });
    backdrop.setAlpha(0);
    this.tweens.add({
      targets: backdrop,
      alpha: 0.58,
      duration: 200,
      ease: 'Sine.easeOut',
    });
    this.tweens.add({
      targets: popupElements,
      scaleX: 1, scaleY: 1,
      alpha: 1,
      duration: 300,
      ease: 'Back.easeOut',
      delay: 50,
    });

    this.time.delayedCall(4500, () => {
      this.destroyRoundEndPopup();
    });
  }
}
